import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { createPortal } from 'react-dom';
import { bajaMarkers, bajaAreas, bajaHikes } from './baja_markers';
import CustomMapTypeControl from './CustomMapTypeControl';
import './App.css';
import PaymentResult from './PaymentResult';
import disclaimerText from './disclaimerText';

// Extend window type for measureInfoWindow
declare global {
  interface Window {
    measureInfoWindow?: any;
  }
}

const logoSeaOfCortez = process.env.PUBLIC_URL + '/images/logos/logo_seaofcortez_ohneschriftzug.svg';
const logoSeaOfCortezMobile = process.env.PUBLIC_URL + '/images/logos/logo_seaofcortez.svg';


// Helper for case-insensitive partial match
function filterMarkersByName(markers: any[], query: string): any[] {
  if (!query) return [];
  const q = query.toLowerCase();
  return markers.filter((m) => m.name.toLowerCase().includes(q));
}

// Centralized group-to-icon mapping
function getGroupIcon(group: string): string {
  const groupLower = group.toLowerCase();
  if (groupLower.includes('anchorage')) return 'images/groupfilter_anchorage.png';
  if (groupLower.includes('marina')) return 'images/groupfilter_marina.png';
  if (groupLower.includes('national park')) return 'images/groupfilter_nationalpark.png';
  if (groupLower.includes('water activit')) return 'images/icon-wateractivities.png';
  if (groupLower.includes('airport')) return 'images/groupfilter_airport.png';
  if (groupLower.includes('diving')) return 'images/groupfilter_diving.png';
  if (groupLower.includes('agents')) return 'images/groupfilter_agency.png';
  if (groupLower.includes('emergency')) return 'images/groupfilter_emergency.png';
  if (groupLower.includes('land')) return 'images/groupfilter_landactivities.png';
  return 'images/icon-1.png';
}

function getStringOrDefault(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function getNumberOrDefault(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 800 || (window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches));
const containerStyle = {
  width: '100vw',
  height: isMobile ? '100dvh' : '100vh',
  boxSizing: 'border-box',
  margin: 0,
  padding: 0,
  overflow: 'visible', // Allow Street View and controls to render properly
};

const center = { lat: 24.0, lng: -110.0 };

// Load Google Maps API key from environment. Do NOT commit your API key to source control.
// For create-react-app-based projects the variable must be prefixed with REACT_APP_
const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

function loadScript(src: string) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });
}

function App() {
  // Determine if the current URL is a Stripe redirect path. Do NOT return early here
  // because React Hooks must be called in the same order on every render.
  const __initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const __isPaymentResultPath = __initialPath === '/payment-success' || __initialPath === '/payment-cancel';
  const __paymentResultType = __initialPath === '/payment-success' ? 'success' : 'cancel';
  const [requestAccessOpen, setRequestAccessOpen] = useState(false);
  // Marker for the first measurement point
  const [measureStartMarker, setMeasureStartMarker] = useState<any>(null);
  // Marker for the last measurement point (endpoint handle)
  const [measureEndMarker, setMeasureEndMarker] = useState<any>(null);
  // --- Distance Measurement Feature ---
  // Add state for measurement mode and points
  const [measuring, setMeasuring] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationActive, setLocationActive] = useState(false);
  // Which measurement mode is active: 'distance' or 'radius'
  const [measurementMode, setMeasurementMode] = useState<'distance' | 'radius' | null>('distance');
  const [showMeasureOptions, setShowMeasureOptions] = useState(false);
  // Radius in meters for radius mode
  const [radiusMeters, setRadiusMeters] = useState<number>(100);
  const radiusCircleRef = useRef<any>(null);
  const radiusLabelRef = useRef<any>(null);
  const radiusArrowRef = useRef<any>(null);
  // Draggable marker used only for radius center (separate from measurePoints)
  const radiusCenterMarkerRef = useRef<any>(null);
  const radiusMetersRef = useRef<number>(radiusMeters);
  useEffect(() => { radiusMetersRef.current = radiusMeters; }, [radiusMeters]);
  // Centralized radius cleanup helper so all UI paths remove the same objects
  const clearRadius = () => {
    try { if (radiusCircleRef.current && radiusCircleRef.current.setMap) { radiusCircleRef.current.setMap(null); radiusCircleRef.current = null; } } catch (e) {}
    try { if (radiusArrowRef.current && radiusArrowRef.current.setMap) { radiusArrowRef.current.setMap(null); radiusArrowRef.current = null; } } catch (e) {}
    try {
      if (radiusLabelRef.current) {
        try {
          if (radiusLabelRef.current.close) radiusLabelRef.current.close();
          else if (radiusLabelRef.current.setMap) radiusLabelRef.current.setMap(null);
          else if ((radiusLabelRef.current as any).remove) (radiusLabelRef.current as any).remove();
        } catch (e) {}
        radiusLabelRef.current = null;
      }
    } catch (e) {}
    // also remove the dedicated draggable center marker for radius (if any)
    try { if (radiusCenterMarkerRef.current && radiusCenterMarkerRef.current.setMap) { radiusCenterMarkerRef.current.setMap(null); radiusCenterMarkerRef.current = null; } } catch (e) {}
  };
  const locationCircleRef = useRef<any>(null);
  const locationActiveRef = useRef(locationActive);
  const measurementModeRef = useRef<any>(measurementMode);
  const showMeasureOptionsRef = useRef(showMeasureOptions);
  useEffect(() => { locationActiveRef.current = locationActive; }, [locationActive]);
  useEffect(() => { measurementModeRef.current = measurementMode; }, [measurementMode]);
  useEffect(() => { showMeasureOptionsRef.current = showMeasureOptions; }, [showMeasureOptions]);
  
  const [measurePoints, setMeasurePoints] = useState<{ lat: number; lng: number }[]>([]);
  const [measureLine, setMeasureLine] = useState<any>(null);
  // Toggle showing individual segment labels
  const [showSegmentLabels, setShowSegmentLabels] = useState(false);
  // Ref to store per-segment InfoWindows so we can clear them
  const measureSegmentWindowsRef = useRef<any[]>([]);
  // Ref to always have latest measuring value in map click handler
  const measuringRef = useRef(measuring);
  useEffect(() => { measuringRef.current = measuring; }, [measuring]);
  // no longer keep measurements when measuring is toggled off
  // State for enlarged carousel image
  const [carouselImgEnlarged, setCarouselImgEnlarged] = useState(false);

  // Feature carousel for login screen
  const featurePoints = [
    {
      text: 'Intuitive Map Experience: Instantly search, filter, and explore hundreds of curated markers, each with detailed descriptions and stunning images.',
  image: process.env.PUBLIC_URL + '/images/welcomescreen/example_markersonmap.JPG',
    },
    { text: 'Responsive Design: Enjoy a seamless experience on desktop and mobile, with controls and features tailored to your device.' },
    { text: 'Smart Search & Filters: Find exactly what you need—by name, category, or location—using our powerful search bar and group filters.',
  image: process.env.PUBLIC_URL + '/images/welcomescreen/example_searchbar.JPG',
     },
    { text: 'Rich Marker Details: Click any marker to view photos, descriptions, and even download a personalized PDF with map snapshots for offline use.',
  image: process.env.PUBLIC_URL + '/images/welcomescreen/example_marker.JPG',
    },
  ];
  const [featureIdx, setFeatureIdx] = useState(0);

  // Reset enlarged image when switching carousel slide
  useEffect(() => {
    setCarouselImgEnlarged(false);
  }, [featureIdx]);

  // Auto-advance feature carousel every 10s (reset on manual navigation)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFeatureIdx(idx => (idx + 1) % featurePoints.length);
    }, 10000);
    return () => clearTimeout(timer);
  }, [featureIdx, featurePoints.length]);
  // Touch swipe state for carousel
  const carouselTouchStart = useRef<number | null>(null);
  const carouselTouchEnd = useRef<number | null>(null);
  // Legal modal state
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalText, setLegalText] = useState('');

  // Fetch legal text from public folder when modal opens
  useEffect(() => {
    if (legalOpen) {
      fetch('/LEGAL.txt')
        .then(res => res.text())
        .then(setLegalText)
        .catch(() => setLegalText('Legal information could not be loaded.'));
    }
  }, [legalOpen]);
  // Helper: Convert decimal degrees to DMS (Grad, Minuten, Sekunden)
  function toDMS(deg: number, isLat: boolean): string {
    const abs = Math.abs(deg);
    const grad = Math.floor(abs);
    const minFloat = (abs - grad) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(2);
    const dir = isLat
      ? deg >= 0 ? 'N' : 'S'
      : deg >= 0 ? 'E' : 'W';
    return `${grad}° ${min}' ${sec}'' ${dir}`;
  }

  // PDF download handler for marker (with map snapshot and English labels)
  async function handleDownloadMarker(marker: { title: string; lat: number; lng: number; description: string }) {
    // Use mm units (jsPDF default). Convert some visual units from px/pt where helpful.
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    // Margins: 50px all around -> convert px to mm (1px ~= 0.264583 mm)
    const margin = 50 * 0.264583; // ~13.23 mm

    // Theme colors (RGB)
    const DEEP_SEA = { r: 1, g: 58, b: 99 }; // #013A63
    const TEAL = { r: 2, g: 128, b: 144 }; // #028090
    const LIGHT_AQUA = { r: 224, g: 251, b: 252 }; // #E0FBFC

    // Font sizes (pt)
    const titleSize = 20;
    const sectionHeaderSize = 14;
    const bodySize = 11;

    // Helper: convert pt to mm for vertical spacing estimates (1pt = 0.352778 mm)
    const ptToMm = (pt: number) => pt * 0.352777778;

    // Load logo (prefer SVG present in repo) and rasterize to PNG data URL for jsPDF
    let logoImgData = '';
    try {
      // Prefer custom .scg logo if present, then try a couple of svg fallbacks, finally PNG
      const candidates = [
        '/images/logos/logo_seaofcortez.svg',
        '/images/logos/logo_seaofcortez_ohneschriftzug.svg',
      ];
      let svgText = '';
      let found = false;
      for (const rel of candidates) {
        const url = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + rel : rel;
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const text = await res.text();
          // quick sanity: treat binary (png) as non-svg if it starts with '�PNG' or similar
          if (text && /<svg[\s>]/i.test(text)) {
            svgText = text;
            found = true;
            break;
          }
        } catch (e) {
          // ignore and continue to next candidate
        }
      }
      if (!found) {
        // fallback to PNG
        try {
          const pngUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/images/logos/logo_seaofcortez.png' : '/images/logos/logo_seaofcortez.png';
          const response = await fetch(pngUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          logoImgData = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          logoImgData = '';
        }
      }
      if (svgText && !logoImgData) {
        // create an image from SVG by drawing into canvas
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new window.Image();
        img.src = url;
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(true);
          img.onerror = reject;
        });
        // draw to canvas with some maximum size to control PDF sizing
        const maxPx = 400;
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, maxPx / Math.max(img.width || maxPx, img.height || maxPx));
        canvas.width = (img.width || maxPx) * scale;
        canvas.height = (img.height || maxPx) * scale;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // ensure transparent background preserved
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          logoImgData = canvas.toDataURL('image/png');
        }
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      logoImgData = '';
    }

    // Space trackers
    let y = margin;

    // Header: logo left, title right
    const headerHeightEstimate = Math.max(ptToMm(titleSize), 18);
    if (logoImgData) {
      const img = new window.Image();
      img.src = logoImgData;
      try {
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(true);
          img.onerror = reject;
        });
      } catch (e) {
        // ignore image load errors
      }
      // Reserve logo size
      const logoMaxH = headerHeightEstimate; // mm
      const logoMaxW = 50; // mm
      let drawW = logoMaxW;
      let drawH = logoMaxH;
      if (img.width && img.height) {
        const aspect = img.width / img.height;
        if (aspect > 1) {
          drawW = Math.min(logoMaxW, logoMaxH * aspect);
          drawH = drawW / aspect;
        } else {
          drawH = Math.min(logoMaxH, logoMaxW / aspect);
          drawW = drawH * aspect;
        }
      }
      doc.addImage(logoImgData, 'PNG', margin, y, drawW, drawH);
    }

    // Title on the same header line, right aligned
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(titleSize);
    doc.setTextColor(DEEP_SEA.r, DEEP_SEA.g, DEEP_SEA.b);
    const titleY = y + ptToMm(titleSize) * 0.8; // baseline offset
    // Right align against right margin
    doc.text(marker.title, pageWidth - margin, titleY, { align: 'right' as any });

    // Move cursor past header
    y += headerHeightEstimate + 6;

    // Thin teal divider under header
    doc.setDrawColor(TEAL.r, TEAL.g, TEAL.b);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    // --- Location Section (map snapshot + coordinates box) ---
    doc.setFontSize(sectionHeaderSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(DEEP_SEA.r, DEEP_SEA.g, DEEP_SEA.b);
    doc.text('Location', margin, y);
    y += ptToMm(sectionHeaderSize) + 4;

    // Fetch static map image
    let mapImgData = '';
    let snapshotError = false;
    try {
      const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${marker.lat},${marker.lng}` +
        `&zoom=11&size=800x600&scale=2&maptype=roadmap&markers=color:red%7C${marker.lat},${marker.lng}&key=${GOOGLE_MAPS_API_KEY}`;
      const response = await fetch(staticMapUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      mapImgData = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      snapshotError = true;
    }

    // Layout: place map on left (60%), coordinates box on right (40%)
    const availW = pageWidth - margin * 2;
    const gap = 6;
    const mapW = availW * 0.62;
    const coordsW = availW - mapW - gap;
    const mapX = margin;
    const mapY = y;
    // compute map height based on actual image aspect ratio to avoid stretching
    let mapH = 70; // default mm
    if (mapImgData) {
      const mapImg = new window.Image();
      mapImg.src = mapImgData;
      try {
        await new Promise((resolve, reject) => {
          mapImg.onload = () => resolve(true);
          mapImg.onerror = reject;
        });
        const imgW = mapImg.width || 4;
        const imgH = mapImg.height || 3;
        const aspect = imgW / imgH; // width / height
        // mapW is in mm, image aspect ratio expects pixels but ratio is same
        mapH = mapW / aspect;
        // cap to sensible min/max to avoid huge or tiny renders
        const maxMapH = 120; // mm
        const minMapH = 40; // mm
        mapH = Math.max(minMapH, Math.min(maxMapH, mapH));
        try {
          doc.addImage(mapImgData, 'PNG', mapX, mapY, mapW, mapH);
        } catch (e) {
          // fallback to fixed height
          try { doc.addImage(mapImgData, 'PNG', mapX, mapY, mapW, 70); mapH = 70; } catch (e) { /* ignore */ }
        }
      } catch (e) {
        // image load failed, draw placeholder
        doc.setDrawColor(200);
        doc.setLineWidth(0.3);
        doc.rect(mapX, mapY, mapW, mapH);
      }
    } else {
      // draw placeholder rectangle
      doc.setDrawColor(200);
      doc.setLineWidth(0.3);
      doc.rect(mapX, mapY, mapW, mapH);
    }

    // Coordinates box — match its height to the rendered map so layout aligns
    const coordsX = mapX + mapW + gap;
    const coordsY = mapY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(TEAL.r, TEAL.g, TEAL.b);
    doc.setLineWidth(0.7);
    try { doc.roundedRect(coordsX, coordsY, coordsW, mapH, 3, 3, 'S'); } catch (e) { doc.rect(coordsX, coordsY, coordsW, mapH); }
    // Text inside box
    doc.setFontSize(sectionHeaderSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(TEAL.r, TEAL.g, TEAL.b);
    const coordsTextX = coordsX + 4;
    let coordsTextY = coordsY + ptToMm(sectionHeaderSize);
    doc.text('Coordinates', coordsTextX, coordsTextY);
    coordsTextY += 4;
    doc.setFontSize(bodySize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(20, 20, 20);
    const decimal = `Decimal: ${marker.lat.toFixed(6)}, ${marker.lng.toFixed(6)}`;
    const latDms = `Latitude: ${toDMS(marker.lat, true)}`;
    const lngDms = `Longitude: ${toDMS(marker.lng, false)}`;
    // splitTextToSize to wrap inside coordsW - padding
    const padding = 4;
    const textW = coordsW - padding * 2;
    const linesDecimal = doc.splitTextToSize(decimal, textW);
    doc.text(linesDecimal, coordsTextX + padding, coordsTextY);
    coordsTextY += ptToMm(bodySize) * linesDecimal.length + 3;
    const linesLat = doc.splitTextToSize(latDms, textW);
    doc.text(linesLat, coordsTextX + padding, coordsTextY);
    coordsTextY += ptToMm(bodySize) * linesLat.length + 2;
    const linesLng = doc.splitTextToSize(lngDms, textW);
    doc.text(linesLng, coordsTextX + padding, coordsTextY);

    // Move y cursor below the map block
    y = mapY + mapH + 10;

    // --- Description Section ---
    doc.setFontSize(sectionHeaderSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(DEEP_SEA.r, DEEP_SEA.g, DEEP_SEA.b);
    doc.text('Description', margin, y);
    y += ptToMm(sectionHeaderSize) + 4;

    // Prepare plain text from HTML-like description
    function htmlToPlainText(html: string) {
      if (!html) return '';
      const withBreaks = html.replace(/<br\s*\/?>(\s*)/gi, '\n');
      const stripped = withBreaks.replace(/<[^>]+>/g, '');
      return stripped.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
    }
    const plain = htmlToPlainText(marker.description || '');

    // Draw shaded box background for description
    const descX = margin;
    const descW = pageWidth - margin * 2;
    // Estimate height needed for description by splitting text into lines
    doc.setFontSize(bodySize);
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(plain, descW - 8);
    const lineH = ptToMm(bodySize) * 1.2;
    const descNeededH = descLines.length * lineH + 10;

    // If not enough room on current page, add page
    const footerReserve = 20; // mm reserved for footer area
    if (y + descNeededH + footerReserve > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }

    // Draw rounded rect filled with light aqua
    doc.setFillColor(LIGHT_AQUA.r, LIGHT_AQUA.g, LIGHT_AQUA.b);
    try { doc.roundedRect(descX, y, descW, descNeededH, 4, 4, 'F'); } catch (e) { doc.rect(descX, y, descW, descNeededH, 'F'); }
    // Draw description text inside
    const textStartY = y + 6;
    let curY = textStartY;
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(bodySize);
    const leftTextX = descX + 6;
    for (let i = 0; i < descLines.length; i++) {
      const line = descLines[i];
      if (curY + lineH > pageHeight - margin - footerReserve) {
        // paginate
        doc.addPage();
        curY = margin + 6;
        // repeat a very small header so reader keeps context (optional)
      }
      doc.text(Array.isArray(line) ? line : String(line), leftTextX, curY);
      curY += lineH;
    }
    y = Math.max(y + descNeededH, curY) + 6;

    // --- Footer ---
    const footerYLine = pageHeight - margin - 12;
    doc.setDrawColor(TEAL.r, TEAL.g, TEAL.b);
    doc.setLineWidth(0.4);
    doc.line(margin, footerYLine, pageWidth - margin, footerYLine);
    const footerText = `Generated by Sea Of Cortez Explorer • seaofcortezexplorer.com • ${new Date().toLocaleDateString()}`;
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(footerText, pageWidth / 2, footerYLine + 6, { align: 'center' as any });

    // Save PDF
    const safeName = (marker.title || 'marker').replace(/[^a-z0-9]/gi, '_');
    doc.save(`${safeName}.pdf`);

    if (snapshotError) {
      alert('Map snapshot could not be included in the PDF. This is a browser or Google Maps limitation.');
    }
  }



  // Touch swipe state for image navigation
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);







  // --- Backend login/session logic ---
  // Place this block after all useState declarations for password, passwordValid, passwordError, sessionToken, and activeGroups
  // (Moved below)

  // Detect if device is touch-capable (mobile/tablet)
  const isTouchDevice = typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
  // Detect if desktop (not touch device)
  const isDesktop = typeof window !== 'undefined' && !isTouchDevice;


  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  // Mobile lightbox zoom/pan state (only for touch devices)
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = React.useState<{ x: number; y: number } | null>(null);
  const [lastTouchDist, setLastTouchDist] = React.useState<number | null>(null);
  const [lastTouchCenter, setLastTouchCenter] = React.useState<{ x: number; y: number } | null>(null);
  const pointerMap = React.useRef<Map<number, PointerEvent>>(new Map());
  React.useEffect(() => {
    if (!isTouchDevice) return;
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, isTouchDevice]);
  // Store marker objects for dynamic filtering
  const markersRef = useRef<any[]>([]);
  // Clusterer ref (for MarkerClusterer)
  const clustererRef = useRef<any>(null);
  // Store area shapes (polygons / polylines) so we can toggle visibility later
  const areaShapesRef = useRef<Array<{ type: string; shape: any; name?: string }>>([]);
  // Store hike shapes so hikes can be toggled like areas
  const hikeShapesRef = useRef<Array<{ type: string; shape: any; name?: string }>>([]);
  // Group filter state (dropdown now tied to search bar focus or filter icon click)
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const searchBarContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Ref for the full marker list dropdown so we can close on outside clicks
  const fullListRef = useRef<HTMLDivElement | null>(null);
  // Extract unique groups from markers (local variable, always up-to-date)
  // Exclude any "agents" group here — agent markers are always visible and
  // should not appear in the group filter controls.
  const uniqueGroups = Array.from(new Set(bajaMarkers.map(m => m.group).filter(Boolean)))
    .filter(g => !(g && g.toLowerCase().includes('agent')));
  const [activeGroups, setActiveGroups] = useState<string[]>([]);
  // store previous groups so we can restore them when custom filter is turned off
  const previousGroupsRef = useRef<string[] | null>(null);
  // Custom filter: user-selected specific markers
  const [customSelectedMarkers, setCustomSelectedMarkers] = useState<string[]>([]);
  const [customFilterActive, setCustomFilterActive] = useState<boolean>(false);
  const [showCustomEditor, setShowCustomEditor] = useState<boolean>(false);
  const [customEditorQuery, setCustomEditorQuery] = useState<string>('');
  // ref for hidden file input used to import JSON
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const toggleGroup = (group: string) => {
    // don't allow toggling groups while custom filter is active
    if (customFilterActive) return;
    setActiveGroups(prev => prev.includes(group)
      ? prev.filter(g => g !== group)
      : [...prev, group]
    );
  };

  // Export current custom selection as JSON file
  const handleExportCustom = () => {
    try {
      const data = { selected: customSelectedMarkers };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'baja_custom_selection.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed.');
    }
  };

  // Trigger hidden file input click
  const handleImportClick = () => {
    if (importInputRef.current) importInputRef.current.value = '';
    if (importInputRef.current) importInputRef.current.click();
  };

  // Handle selected JSON file and apply import
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        let parsed: any = null;
        try { parsed = JSON.parse(text); } catch (err) { throw new Error('Invalid JSON file.'); }
        let arr: any[] = [];
        if (Array.isArray(parsed)) arr = parsed;
        else if (parsed && Array.isArray(parsed.selected)) arr = parsed.selected;
        else throw new Error('JSON must be an array of marker names or an object { selected: [...] }.');
        const validNames = new Set(bajaMarkers.map(m => m.name));
        const filtered = arr.filter((x: any) => typeof x === 'string' && validNames.has(x));
        if (filtered.length === 0) {
          alert('No valid marker names found in the imported file.');
          return;
        }
        setCustomSelectedMarkers(filtered as string[]);
        // enable custom filter and switch to hide-all mode if not already active
        if (!customFilterActive) {
          previousGroupsRef.current = activeGroups.slice();
          setActiveGroups([]);
          setCustomFilterActive(true);
        }
        alert(`Imported ${filtered.length} marker(s) into custom selection.`);
      } catch (err: any) {
        alert('Import failed: ' + (err && err.message ? err.message : String(err)));
      }
    };
    reader.onerror = () => { alert('Failed to read file.'); };
    reader.readAsText(file);
  };
  const mapRef = useRef<HTMLDivElement>(null);
  const [streetViewActive, setStreetViewActive] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const [password, setPassword] = React.useState("");
  // Request Access UI state
  const [accessStep, setAccessStep] = useState<'select'|'email'|'done'>('select');
  const [accessPlan, setAccessPlan] = useState<string | null>(null);
  const [accessEmail, setAccessEmail] = useState<string>('');
  const [accessMessage, setAccessMessage] = useState<string>('');
  const [accessLoading, setAccessLoading] = useState<boolean>(false);

  // --- Backend login/session logic ---
  // Place this block after all useState declarations for password, passwordValid, passwordError, sessionToken, and activeGroups
  // (Moved here to ensure handleLogin is defined before useEffect uses it)
// --- Backend login/session logic ---
// Set your API base URL here for local or production
//const API_BASE_URL = 'http://localhost:4000'// 'https://bajacaliforniaguide.onrender.com';
const API_BASE_URL = 'https://bajacaliforniaguide.onrender.com';

// Helper: show a centered, styled disclaimer modal using DOM so we don't need to change JSX
function showDisclaimerModal(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve(true);
    // avoid duplicate modals
    if (document.getElementById('baja-disclaimer-modal')) return resolve(true);
    const overlay = document.createElement('div');
    overlay.id = 'baja-disclaimer-modal';
    Object.assign(overlay.style, {
      position: 'fixed', left: '0', top: '0', right: '0', bottom: '0',
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: '20000', padding: '20px',
    });

    const box = document.createElement('div');
    Object.assign(box.style, {
      maxWidth: '820px', width: '100%', maxHeight: '80vh', overflowY: 'auto',
      background: '#fff', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', padding: '22px',
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial', color: '#102a43',
    });

    const title = document.createElement('h2');
    title.textContent = 'Disclaimer';
    Object.assign(title.style, { marginTop: '0', marginBottom: '12px', color: '#013A63' });

  const text = document.createElement('div');
  text.style.whiteSpace = 'pre-wrap';
  text.style.lineHeight = '1.45';
  text.style.fontSize = '14px';
  text.textContent = disclaimerText;

    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' });

    const disagree = document.createElement('button');
    disagree.textContent = 'Disagree';
    Object.assign(disagree.style, {
      padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer'
    });

    const agree = document.createElement('button');
    agree.textContent = 'Agree';
    Object.assign(agree.style, {
      padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer'
    });

    btnRow.appendChild(disagree);
    btnRow.appendChild(agree);

    box.appendChild(title);
    box.appendChild(text);
    box.appendChild(btnRow);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    function cleanup(result: boolean) {
      try { document.removeEventListener('keydown', onKey); } catch (e) {}
      try { overlay.remove(); } catch (e) {}
      resolve(result);
    }

    agree.addEventListener('click', () => cleanup(true));
    disagree.addEventListener('click', () => cleanup(false));
    // keyboard accessibility: Escape = disagree, Enter = agree
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { cleanup(false); }
      if (e.key === 'Enter') { cleanup(true); }
    }
    document.addEventListener('keydown', onKey);
  });
}

const handleLogin = React.useCallback(() => {
  fetch(`${API_BASE_URL}/api/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
    .then(async res => {
      let data;
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (data.success && data.sessionToken) {
        setPasswordValid(true);
        setSessionToken(data.sessionToken);
        localStorage.setItem('baja_password', password);
        localStorage.setItem('baja_sessionToken', data.sessionToken);
            // Show disclaimer if not previously accepted. If declined, clear session and return to login.
            try {
              const alreadyAccepted = localStorage.getItem('baja_disclaimer_accepted') === 'true';
              if (!alreadyAccepted) {
                const accepted = await showDisclaimerModal();
                if (accepted) {
                  try { localStorage.setItem('baja_disclaimer_accepted', 'true'); } catch (e) {}
                } else {
                  // User declined: clear session and return to the login state
                  setPasswordValid(false);
                  setSessionToken(null);
                  try { localStorage.removeItem('baja_sessionToken'); } catch (e) {}
                  try { localStorage.removeItem('baja_password'); } catch (e) {}
                  try { window.history.replaceState({}, '', window.location.pathname); } catch (e) {}
                  try { window.alert('You must accept the disclaimer to use this site. You have been returned to the login screen.'); } catch (e) {}
                }
              }
            } catch (e) {}
      } else {
        // Show backend error message if available
        if (data.blocked) {
          setPasswordError(data.message || 'Too many failed login attempts. Please try again later.');
        } else {
          setPasswordError(data.message || 'Incorrect password.');
        }
        setSessionToken(null);
        localStorage.removeItem('baja_password');
        localStorage.removeItem('baja_sessionToken');
      }
      setSessionLoading(false);
    })
    .catch(() => {
      setPasswordError('Server error. Please try again.');
      setSessionLoading(false);
    });
}, [password]);
  const [passwordValid, setPasswordValid] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState("");
  const [sessionToken, setSessionToken] = React.useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = React.useState(true);
  // On mount, set password from qrpassword param if present, else from localStorage
  const passwordInitialized = useRef(false);
  const [qrLoginPending, setQrLoginPending] = useState(false);
  useEffect(() => {
    if (passwordInitialized.current) return;
    let initialPassword = '';
    let foundQrPassword = false;
    const params = new URLSearchParams(window.location.search);
    const qrPassword = params.get('qrpassword');
    if (qrPassword) {
      initialPassword = qrPassword;
      foundQrPassword = true;
      params.delete('qrpassword');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    } else {
      initialPassword = localStorage.getItem('baja_password') || '';
    }
    setPassword(initialPassword);
    setPasswordValid(false);
    setSessionToken(null);
    setSessionLoading(false);
    localStorage.removeItem('baja_sessionToken');
    passwordInitialized.current = true;
    if (foundQrPassword) {
      setQrLoginPending(true);
    }
  }, []);

  // Ensure the measure/search popup sits below the search bar on small screens
  useEffect(() => {
    if (!isMobile) return;
    function positionMeasurePopup() {
      try {
        const popup = document.querySelector('.search-mode-popup') as HTMLElement | null;
        if (!popup) return;
        // Prefer the ref, but fall back to querying the visible search bar element
        let bar = searchBarContainerRef.current as HTMLElement | null;
        if (!bar) bar = document.querySelector('.marker-search-bar') as HTMLElement | null;
        const gap = 14; // increased gap so popup doesn't overlap the bar
        if (bar) {
          const rect = bar.getBoundingClientRect();
          const top = Math.max(rect.bottom + gap, 56 + gap);
          popup.style.setProperty('top', `${top}px`, 'important');
          popup.style.setProperty('position', 'fixed', 'important');
          popup.style.setProperty('left', '50%', 'important');
          popup.style.setProperty('transform', 'translateX(-50%)', 'important');
          popup.style.setProperty('z-index', '20020', 'important');
        } else {
          // fallback: keep a safe top offset
          popup.style.setProperty('top', `calc(100% + ${gap}px)`, 'important');
        }
      } catch (e) {}
    }
    positionMeasurePopup();
    window.addEventListener('resize', positionMeasurePopup);
    window.addEventListener('orientationchange', positionMeasurePopup);
    // also reposition when virtual keyboard may appear (focus events)
    window.addEventListener('focusin', positionMeasurePopup);
    return () => {
      window.removeEventListener('resize', positionMeasurePopup);
      window.removeEventListener('orientationchange', positionMeasurePopup);
      window.removeEventListener('focusin', positionMeasurePopup);
    };
  }, [showMeasureOptions, measuring]);

  // Effect to trigger login only after password is set and qrLoginPending is true
  useEffect(() => {
    if (qrLoginPending && password) {
      setQrLoginPending(false);
      handleLogin();
    }
  // Add handleLogin to the dependency array
  }, [qrLoginPending, password, handleLogin]);
  // (Removed duplicate sessionToken and sessionLoading declarations)
  const [search, setSearch] = React.useState("");
  const [searchActive, setSearchActive] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  // when user requests the full marker list from the search-mode popup
  const [showAllMarkersList, setShowAllMarkersList] = React.useState(false);
  // Which groups are expanded in the full markers list (when viewing all markers)
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>([]);
  // Warning modal when no markers available due to filters
  const [noMarkersWarningOpen, setNoMarkersWarningOpen] = React.useState(false);
  const [searchMode, setSearchMode] = React.useState<'markers' | 'locations'>('markers');
  const [showSearchButtons, setShowSearchButtons] = React.useState(false);
  // Toggle to enable/disable marker clustering on the map
  const [clusteringEnabled, setClusteringEnabled] = React.useState<boolean>(true);
  // remember if measure popup was open before opening search/group UI so we can restore it
  const measurePopupWasOpenBeforeUiRef = useRef<boolean>(false);

  // When search becomes active, hide measure popup and remember previous state; restore when search closes
  useEffect(() => {
    try {
      if (searchActive) {
        measurePopupWasOpenBeforeUiRef.current = showMeasureOptionsRef.current;
        setShowMeasureOptions(false);
      } else {
        if (measuring && measurePopupWasOpenBeforeUiRef.current) {
          setShowMeasureOptions(true);
          measurePopupWasOpenBeforeUiRef.current = false;
        }
      }
    } catch (e) {}
  }, [searchActive, measuring]);

  // When group dropdown opens, hide measure popup and remember previous state; restore when it closes
  useEffect(() => {
    try {
      if (showGroupDropdown) {
        measurePopupWasOpenBeforeUiRef.current = showMeasureOptionsRef.current;
        setShowMeasureOptions(false);
      } else {
        if (measuring && measurePopupWasOpenBeforeUiRef.current) {
          setShowMeasureOptions(true);
          measurePopupWasOpenBeforeUiRef.current = false;
        }
      }
    } catch (e) {}
  }, [showGroupDropdown, measuring]);
  // Places services refs
  const placesServiceRef = useRef<any>(null);
  const autocompleteServiceRef = useRef<any>(null);
  // Temporary location marker when selecting a place
  const locationMarkerRef = useRef<any>(null);
  const locationMarkerListenerRef = useRef<any>(null);
  const [imgIdx, setImgIdx] = React.useState(0);
  const justOpenedRef = React.useRef(false);
  const [popup, setPopup] = React.useState<null | { title: string; images: string[]; description: string }>(null);
  // Separate PDFs from displayable images for popup UI (do not modify baja_markers.ts)
  const popupNonPdfImages = popup && popup.images && Array.isArray(popup.images)
    ? popup.images.filter((u: string) => !/\.pdf(\?.*)?$/i.test(u))
    : [];
  const popupPdfDocs = popup && popup.images && Array.isArray(popup.images)
    ? popup.images.filter((u: string) => /\.pdf(\?.*)?$/i.test(u))
    : [];
  const [desktopZoomed, setDesktopZoomed] = useState(false);
  // Ref for the lightbox container
  const lightboxContainerRef = useRef<HTMLDivElement>(null);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showGroupDropdown) return;
    function handleClick(e: MouseEvent | TouchEvent) {
      if (
        searchBarContainerRef.current &&
        !searchBarContainerRef.current.contains(e.target as Node)
      ) {
        setShowGroupDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showGroupDropdown]);

  // Close full marker list on outside clicks/touches
  useEffect(() => {
    if (!showAllMarkersList) return;
    function handleOutside(e: MouseEvent | TouchEvent) {
      if (fullListRef.current && !fullListRef.current.contains(e.target as Node)) {
        setShowAllMarkersList(false);
        setShowSearchButtons(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showAllMarkersList]);

  // Responsive CSS for search bar
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media (max-width: 600px) {
        .marker-search-bar {
          top: 56px !important;
          left: 0 !important;
          right: 0 !important;
          width: 100vw !important;
          max-width: 100vw !important;
          border-radius: 0 0 12px 12px !important;
          margin: 0 !important;
          transform: none !important;
        }
      }
      @media (min-width: 601px) {
        .marker-search-bar {
          top: 56px !important;
          left: 50% !important;
          right: auto !important;
          width: 520px !important;
          max-width: 90vw !important;
          border-radius: 10px !important;
          margin: 0 !important;
          transform: translateX(-50%) !important;
        }
        /* Center the filter / measure / locate icons group on desktop. We wrap
           those three controls in a .search-icons container and center it. */
        .marker-search-bar .search-icons {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          gap: 8px;
          align-items: center;
          pointer-events: auto;
        }
      }
      .search-mode-popup {
        position: absolute;
        left: 0;
        top: 100%;
        margin-top: 8px;
        min-width: 210px;
        background: rgba(255,255,255,0.85);
        border: 1.5px solid #e0e7ef;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(25, 118, 210, 0.10), 0 1.5px 8px rgba(0,0,0,0.07);
        padding: 16px 18px 12px 18px;
        z-index: 10010;
        display: flex;
        flex-direction: column;
        gap: 14px;
        align-items: stretch;
        backdrop-filter: blur(8px);
        animation: fadeInPopup 0.25s cubic-bezier(.4,0,.2,1);
      }

      /* Mobile: center and constrain the measure/search popup so it fits on small screens */
      @media (max-width: 600px) {
        .search-mode-popup {
          position: fixed !important;
          left: 50% !important;
          right: auto !important;
          transform: translateX(-50%) !important;
          min-width: auto !important;
          width: calc(100vw - 24px) !important;
          max-width: calc(100vw - 24px) !important;
          border-radius: 12px !important;
          top: auto !important;
          padding: 12px !important;
          box-sizing: border-box;
          margin-top: 0 !important;
        }
      }
      @keyframes fadeInPopup {
        from { opacity: 0; transform: translateY(10px) scale(0.98); }
        to { opacity: 1; transform: none; }
      }
      .search-mode-popup button {
        font-size: 16px;
        font-weight: 500;
        padding: 10px 18px 10px 14px;
        border-radius: 10px;
        border: 1.5px solid #e0e7ef;
        background: rgba(255,255,255,0.7);
        color: #1976d2;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 10px;
        box-shadow: 0 1px 4px rgba(25, 118, 210, 0.04);
        transition: background 0.18s, color 0.18s, border 0.18s, box-shadow 0.18s;
        outline: none;
      }
      .search-mode-popup button.active {
        border: 1.5px solid #1976d2;
        background: #1976d2;
        /* active buttons should show white text for readability */
        color: #fff;
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.10);
      }
      .search-mode-popup button:hover:not(.active),
      .search-mode-popup button:focus-visible:not(.active) {
        background: #e3eaf6;
        color: #1565c0;
        border: 1.5px solid #90caf9;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Hide search mode popup on outside click or when typing
  React.useEffect(() => {
    function handleClick(e: MouseEvent | TouchEvent) {
      if (
        searchBarContainerRef.current &&
        !searchBarContainerRef.current.contains(e.target as Node)
      ) {
        setShowSearchButtons(false);
      }
    }
    if (showSearchButtons) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('touchstart', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showSearchButtons]);

  // Imperatively block gesture events on popup overlay/content to suppress browser magnifier
  React.useEffect(() => {
    const popupEl = popupRef.current;
    if (!popupEl) return;
    function prevent(e: Event) { e.preventDefault(); }
    popupEl.addEventListener('gesturestart', prevent);
    return () => { popupEl.removeEventListener('gesturestart', prevent); };
  }, [popup]);

  // Marker search: immediate filtering
  React.useEffect(() => {
    if (searchMode === 'markers') {
      const filteredMarkers = customFilterActive
        ? bajaMarkers.filter(m => customSelectedMarkers.includes(m.name))
        : bajaMarkers.filter(m => activeGroups.includes(m.group));
      if (search) setSearchResults(filterMarkersByName(filteredMarkers, search));
      else setSearchResults([]);
    }
    // when switching modes away from markers, don't change results here
  }, [search, activeGroups, searchMode, customFilterActive, customSelectedMarkers]);

  // Location search: debounced predictions (200ms)
  React.useEffect(() => {
    if (searchMode !== 'locations') return;
    let cancelled = false;
    if (!search || !autocompleteServiceRef.current) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(() => {
      try {
        autocompleteServiceRef.current.getPlacePredictions({ input: search, types: ['(cities)'] }, (preds: any[], status: any) => {
          if (cancelled) return;
      if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && preds && preds.length) {
            setSearchResults(preds.map(p => ({ description: p.description, place_id: p.place_id })));
          } else {
            setSearchResults([]);
          }
        });
      } catch (e) {
        if (!cancelled) setSearchResults([]);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [search, searchMode]);

  // Map initialization and marker/area rendering
  // Map initialization (runs once)
  React.useEffect(() => {
    let map: any = null;
    // area shapes are stored in areaShapesRef.current (kept across renders)
    let measureLineObj: any = null;
    const initMap = async () => {
      try {
        await loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,marker,places`
        );
        let waited = 0;
        while ((!window.google || !window.google.maps) && waited < 2000) {
          await new Promise((res) => setTimeout(res, 50));
          waited += 50;
        }
        if (!window.google || !window.google.maps) {
          alert('Google Maps JS API failed to load. Check your API key, billing, and API activation.');
          return;
        }
        // @ts-ignore
        const { Map } = window.google.maps;
        if (mapRef.current) {
          let mapOptions: any = {
            center,
            zoom: 10,
            mapId: 'ed9ff37d850ba88f8c4f30fd',
            gestureHandling: 'greedy',
            // Always hide the default mapTypeControl, use only CustomMapTypeControl
            mapTypeControl: false,
          };
          map = new Map(mapRef.current, mapOptions);
          mapInstance.current = map;
          // Initialize Places services after google maps loaded
          try {
            autocompleteServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
            placesServiceRef.current = new (window as any).google.maps.places.PlacesService(map);
          } catch (e) {
            // ignore if places lib not available
          }

          // --- Distance Measurement Feature: Map Click Handler ---
          map.addListener('click', (e: any) => {
            // remove temporary location marker when clicking anywhere, but keep it if user enabled persistent location
            if (locationMarkerRef.current && !locationActiveRef.current) {
                try { if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; } } catch (e) {}
                locationMarkerRef.current.setMap(null);
                locationMarkerRef.current = null;
              }
            if (!measuringRef.current) return;
            const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
            // If in radius mode, draw a circle at the clicked location with the chosen radius
            if (measurementModeRef.current === 'radius') {
              try {
                // remove previous circle, arrow and label to avoid leftovers
                try { clearRadius(); } catch (e) {}
                // @ts-ignore
                const { Circle, InfoWindow, Polyline } = window.google.maps;
                const circle = new Circle({
                  map: mapInstance.current,
                  center: latLng,
                  radius: Math.max(0, Number(radiusMetersRef.current) || 0),
                  strokeColor: '#00c853',
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  fillColor: '#00c853',
                  fillOpacity: 0.3,
                  zIndex: 99990,
                });
                radiusCircleRef.current = circle;
                // create/update a small info label showing the radius
                try {
                  if (radiusLabelRef.current && radiusLabelRef.current.close) {
                    radiusLabelRef.current.close();
                    radiusLabelRef.current = null;
                  }
                  // compute an endpoint on the circle (bearing 45°) and midpoint for the arrow
                  let midPos = latLng;
                  try {
                    const bearing = 45; // NE direction
                    const end = window.google.maps.geometry.spherical.computeOffset(new window.google.maps.LatLng(latLng.lat, latLng.lng), Math.max(0, Number(radiusMetersRef.current) || 0), bearing);
                    const mid = window.google.maps.geometry.spherical.computeOffset(new window.google.maps.LatLng(latLng.lat, latLng.lng), Math.max(0, Number(radiusMetersRef.current) || 0) / 2, bearing);
                    midPos = { lat: mid.lat(), lng: mid.lng() };
                    // remove previous arrow if any
                    try { if (radiusArrowRef.current && radiusArrowRef.current.setMap) { radiusArrowRef.current.setMap(null); radiusArrowRef.current = null; } } catch (e) {}
                    // draw arrow polyline from center to end
                    const arrowLine = new Polyline({
                      map: mapInstance.current,
                      path: [latLng, { lat: end.lat(), lng: end.lng() }],
                      strokeColor: '#000000',
                      strokeOpacity: 1,
                      strokeWeight: 2,
                      zIndex: 99991,
                      icons: [{
                        icon: {
                          path: 'M 0,-1 L 6,0 L 0,1 z',
                          fillColor: '#000000',
                          fillOpacity: 1,
                          strokeWeight: 0,
                          scale: 1,
                        },
                        offset: '100%'
                      }]
                    });
                    radiusArrowRef.current = arrowLine;
                  } catch (e) {
                    // geometry library may be unavailable; fallback to label at center
                    midPos = latLng;
                  }
                  // Prefer AdvancedMarkerElement for inline label (no popup arrow)
                  try {
                    // @ts-ignore
                    const { AdvancedMarkerElement } = window.google.maps.marker;
                    const container = document.createElement('div');
                    // Minimal, centered label so it overlays the polyline
                    container.style.display = 'inline-block';
                    container.style.padding = '0 6px';
                    container.style.borderRadius = '4px';
                    container.style.background = 'transparent';
                    container.style.border = 'none';
                    container.style.fontSize = '12px';
                    container.style.fontWeight = '700';
                    container.style.color = '#000000';
                    // subtle white outline for readability over map lines
                    container.style.textShadow = '0 0 2px rgba(255,255,255,0.95)';
                    container.style.pointerEvents = 'none';
                    // center the element on the marker position
                    container.style.transform = 'translate(-50%, -50%)';
                    container.style.whiteSpace = 'nowrap';
                    container.innerText = `${Math.round(Math.max(0, Number(radiusMetersRef.current) || 0))} m`;
                    const adv = new AdvancedMarkerElement({
                      map: mapInstance.current,
                      position: midPos,
                      content: container,
                      zIndex: 99992,
                    });
                    radiusLabelRef.current = adv;
                  } catch (e) {
                    // Fallback to InfoWindow if AdvancedMarkerElement not available
                    const label = new InfoWindow({
                      content: `<div style='font-size:13px;font-weight:700;color:#000000;background:#fff;padding:6px 10px;border-radius:10px;border:1px solid #e6eefc;transform:translateY(-4px)'>${Math.round(Math.max(0, Number(radiusMetersRef.current) || 0))} m</div>`,
                      position: midPos,
                      disableAutoPan: true,
                    });
                    label.open({ map: mapInstance.current });
                    radiusLabelRef.current = label;
                  }
                } catch (e) {}
                // create a dedicated draggable center marker for the radius so we don't overwrite distance measurements
                try { if (radiusCenterMarkerRef.current && radiusCenterMarkerRef.current.setMap) { radiusCenterMarkerRef.current.setMap(null); radiusCenterMarkerRef.current = null; } } catch (e) {}
                try {
                  const { Marker } = window.google.maps;
                  const centerMarker = new Marker({
                    map: mapInstance.current,
                    position: latLng,
                    icon: {
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 12,
                      fillColor: '#388e3c',
                      fillOpacity: 1,
                      strokeColor: '#fff',
                      strokeWeight: 3,
                    },
                    draggable: true,
                    clickable: false,
                    zIndex: 1000000,
                  });
                  // While dragging, update visuals live (do not update measurePoints here)
                  centerMarker.addListener('drag', (ev: any) => {
                    try {
                      const newPos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
                      if (radiusCircleRef.current && radiusCircleRef.current.setCenter) {
                        radiusCircleRef.current.setCenter(newPos);
                      }
                      try {
                        const bearing = 45;
                        const radius = Math.max(0, Number(radiusMetersRef.current) || 0);
                        const end = window.google.maps.geometry.spherical.computeOffset(new window.google.maps.LatLng(newPos.lat, newPos.lng), radius, bearing);
                        const mid = window.google.maps.geometry.spherical.computeOffset(new window.google.maps.LatLng(newPos.lat, newPos.lng), radius / 2, bearing);
                        const midPos = { lat: mid.lat(), lng: mid.lng() };
                        if (radiusArrowRef.current && radiusArrowRef.current.setPath) {
                          radiusArrowRef.current.setPath([newPos, { lat: end.lat(), lng: end.lng() }]);
                        } else {
                          try { if (radiusArrowRef.current && radiusArrowRef.current.setMap) { radiusArrowRef.current.setMap(null); radiusArrowRef.current = null; } } catch (e) {}
                          const { Polyline } = window.google.maps;
                          const arrowLine = new Polyline({
                            map: mapInstance.current,
                            path: [newPos, { lat: end.lat(), lng: end.lng() }],
                            strokeColor: '#1976d2',
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            zIndex: 99991,
                            icons: [{ icon: { path: 'M 0,-1 L 6,0 L 0,1 z', fillColor: '#1976d2', fillOpacity: 1, strokeWeight: 0, scale: 1 }, offset: '100%' }]
                          });
                          radiusArrowRef.current = arrowLine;
                        }
                        // recreate label so it follows marker
                        try {
                          if (radiusLabelRef.current) {
                            try {
                              if (radiusLabelRef.current.close) radiusLabelRef.current.close();
                              else if (radiusLabelRef.current.setMap) radiusLabelRef.current.setMap(null);
                              else if ((radiusLabelRef.current as any).remove) (radiusLabelRef.current as any).remove();
                            } catch (e) {}
                            radiusLabelRef.current = null;
                          }
                        } catch (e) {}
                        try {
                          const { AdvancedMarkerElement } = window.google.maps.marker || {};
                          if (AdvancedMarkerElement) {
                            const container = document.createElement('div');
                            container.style.display = 'inline-block';
                            container.style.padding = '0 6px';
                            container.style.borderRadius = '4px';
                            container.style.background = 'transparent';
                            container.style.border = 'none';
                            container.style.fontSize = '12px';
                            container.style.fontWeight = '700';
                            container.style.color = '#0b3d91';
                            container.style.textShadow = '0 0 2px rgba(255,255,255,0.95)';
                            container.style.pointerEvents = 'none';
                            container.style.transform = 'translate(-50%, -50%)';
                            container.style.whiteSpace = 'nowrap';
                            container.innerText = `${Math.round(Math.max(0, Number(radiusMetersRef.current) || 0))} m`;
                            const adv = new AdvancedMarkerElement({ map: mapInstance.current, position: midPos, content: container, zIndex: 99992 });
                            radiusLabelRef.current = adv;
                          } else {
                            const { InfoWindow } = window.google.maps;
                            const label = new InfoWindow({ content: `<div style='font-size:13px;font-weight:700;color:#0b3d91;background:#fff;padding:6px 10px;border-radius:10px;border:1px solid #e6eefc;transform:translateY(-4px)'>${Math.round(Math.max(0, Number(radiusMetersRef.current) || 0))} m</div>`, position: midPos, disableAutoPan: true });
                            label.open({ map: mapInstance.current });
                            radiusLabelRef.current = label;
                          }
                        } catch (e) {}
                      } catch (e) {}
                    } catch (err) {}
                  });
                  // On drag end, finalize circle center; do not modify measurePoints
                  centerMarker.addListener('dragend', (ev: any) => {
                    try {
                      const finalPos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
                      if (radiusCircleRef.current && radiusCircleRef.current.setCenter) radiusCircleRef.current.setCenter(finalPos);
                    } catch (e) {}
                  });
                  radiusCenterMarkerRef.current = centerMarker;
                } catch (err) {
                  // ignore marker creation errors
                }
              } catch (err) {
                console.error('Error drawing radius circle', err);
              }
              return;
            }
            // Default: distance measurement — append measurement point
            setMeasurePoints((prev) => {
              const tol = 1e-6;
              if (!prev || prev.length === 0) return [latLng];
              // avoid appending duplicate consecutive points
              const last = prev[prev.length - 1];
              if (Math.abs(last.lat - latLng.lat) < tol && Math.abs(last.lng - latLng.lng) < tol) return prev;
              // append new point to build a multi-segment measurement
              return [...prev, latLng];
            });
          });
        }
        // Render polygons and polylines from bajaAreas (once)
        if (map) {
          // Listen for Street View activation and toggle UI hiding
          try {
            const panorama = map.getStreetView();
            if (panorama) {
              const svListener = panorama.addListener('visible_changed', () => {
                const visible = panorama.getVisible && panorama.getVisible();
                setStreetViewActive(!!visible);
              });
              // Ensure UI is hidden if Street View already visible
              if (panorama.getVisible && panorama.getVisible()) setStreetViewActive(true);
              // Store listener on map instance for cleanup
              // @ts-ignore
              map.__streetViewListener = svListener;
            }
          } catch (e) {
            // ignore if panorama not available
          }
          // @ts-ignore
          const { Polygon, Polyline } = window.google.maps;
          bajaAreas.forEach((area) => {
            if (!area.coordinates || area.coordinates.length < 2) return;
            if (area.type === 'polygon') {
              const strokeColor = getStringOrDefault(area.strokeColor, '#1976d2');
              const strokeOpacity = getNumberOrDefault(area.strokeOpacity, 0.8);
              const strokeWeight = getNumberOrDefault(area.strokeWeight, 2);
              const fillColor = getStringOrDefault(area.fillColor, strokeColor);
              const fillOpacity = getNumberOrDefault(area.fillOpacity, 0.15);
              // create polygon but do not add to map yet; visibility will be controlled by group filter
              const poly = new Polygon({
                map: null,
                paths: area.coordinates,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor,
                fillOpacity,
                clickable: !!area.description,
              });
              poly.addListener('click', () => {
                setPopup({
                  title: area.name,
                  images: area.images && Array.isArray(area.images) ? area.images : [],
                  description: area.description
                });
                setImgIdx(0);
                setLightboxOpen(false);
                justOpenedRef.current = true;
                setTimeout(() => { justOpenedRef.current = false; }, 350);
              });
              areaShapesRef.current.push({ type: 'polygon', shape: poly, name: area.name });
            } else if (area.type === 'line') {
              const strokeColor = getStringOrDefault(area.strokeColor, '#d32f2f');
              const strokeOpacity = getNumberOrDefault(area.strokeOpacity, 0.9);
              const strokeWeight = getNumberOrDefault(area.strokeWeight, 3);
              // create polyline but do not add to map yet; visibility will be controlled by group filter
              const line = new Polyline({
                map: null,
                path: area.coordinates,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                clickable: !!area.description,
              });
              line.addListener('click', () => {
                setPopup({
                  title: area.name,
                  images: area.images && Array.isArray(area.images) ? area.images : [],
                  description: area.description
                });
                setImgIdx(0);
                setLightboxOpen(false);
                justOpenedRef.current = true;
                setTimeout(() => { justOpenedRef.current = false; }, 350);
              });
              areaShapesRef.current.push({ type: 'line', shape: line, name: area.name });
            }
          });
          // Create hike shapes (lines/polygons) from `bajaHikes` but do not add to map yet; visibility controlled by group filter
          bajaHikes.forEach((hike) => {
            if (!hike.coordinates || hike.coordinates.length < 2) return;
            if (hike.type === 'polygon') {
              const strokeColor = getStringOrDefault(hike.strokeColor, '#388e3c');
              const strokeOpacity = getNumberOrDefault(hike.strokeOpacity, 0.9);
              const strokeWeight = getNumberOrDefault(hike.strokeWeight, 2);
              const fillColor = getStringOrDefault((hike as any).fillColor, strokeColor);
              const fillOpacity = getNumberOrDefault((hike as any).fillOpacity, 0.12);
              const poly = new Polygon({
                map: null,
                paths: hike.coordinates,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor,
                fillOpacity,
                clickable: !!hike.description,
              });
              poly.addListener('click', () => {
                setPopup({ title: hike.name, images: hike.images && Array.isArray(hike.images) ? hike.images : [], description: hike.description });
                setImgIdx(0);
                setLightboxOpen(false);
                justOpenedRef.current = true;
                setTimeout(() => { justOpenedRef.current = false; }, 350);
              });
              hikeShapesRef.current.push({ type: 'polygon', shape: poly, name: hike.name });
            } else {
              const strokeColor = getStringOrDefault(hike.strokeColor, '#388e3c');
              const strokeOpacity = getNumberOrDefault(hike.strokeOpacity, 0.9);
              const strokeWeight = getNumberOrDefault(hike.strokeWeight, 2);
              const line = new Polyline({
                map: null,
                path: hike.coordinates,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                clickable: !!hike.description,
              });
              line.addListener('click', () => {
                setPopup({ title: hike.name, images: hike.images && Array.isArray(hike.images) ? hike.images : [], description: hike.description });
                setImgIdx(0);
                setLightboxOpen(false);
                justOpenedRef.current = true;
                setTimeout(() => { justOpenedRef.current = false; }, 350);
              });
              hikeShapesRef.current.push({ type: 'line', shape: line, name: hike.name });
            }
            // No start marker created here — marker icons are defined in `bajaMarkers`.
          });
        }
      } catch (e) {
        alert('Google Maps JS API failed to load. Check your API key, billing, and API activation.');
      }
    };
    initMap();
    return () => {
      // Clean up area shapes if needed
      try { areaShapesRef.current.forEach(a => { try { if (a && a.shape && a.shape.setMap) a.shape.setMap(null); } catch (e) {} }); areaShapesRef.current = []; } catch (e) {}
      try { hikeShapesRef.current.forEach(h => { try { if (h && h.shape && h.shape.setMap) h.shape.setMap(null); } catch (e) {} }); hikeShapesRef.current = []; } catch (e) {}
      if (measureLineObj) measureLineObj.setMap(null);
      // remove street view listener if stored on map instance
      try {
        if (map && (map as any).__streetViewListener) {
          (map as any).__streetViewListener.remove();
          (map as any).__streetViewListener = null;
        }
      } catch (e) {}
    };
  }, []);

  // Toggle visibility of area shapes when the National Park Information group is toggled
  React.useEffect(() => {
    // mapInstance is a mutable ref and should not be included in deps (it doesn't trigger re-renders)
    // Show park area shapes if either the category filter is active OR the custom filter contains any park markers
    if (!mapInstance.current || !window.google || !window.google.maps) return;
    try {
      const parkNames = bajaMarkers.filter(m => m.group === 'National Park Information').map(m => m.name);
      const parkSelectedInCustom = customFilterActive && parkNames.some(n => customSelectedMarkers.includes(n));
      const show = activeGroups.includes('National Park Information') || parkSelectedInCustom;
      areaShapesRef.current.forEach(a => {
        try {
          if (a && a.shape && a.shape.setMap) {
            a.shape.setMap(show ? mapInstance.current : null);
          }
        } catch (e) {}
      });
    } catch (e) {}
    // include custom selection flags so area visibility updates when custom selection changes
  }, [activeGroups, customFilterActive, customSelectedMarkers]);

// Toggle visibility of hike shapes when the Land Activities group is toggled
React.useEffect(() => {
  if (!mapInstance.current || !window.google || !window.google.maps) return;
  try {
    const landNames = bajaMarkers.filter(m => m.group === 'Land Activities').map(m => m.name);
    const landSelectedInCustom = customFilterActive && landNames.some(n => customSelectedMarkers.includes(n));
    const show = activeGroups.includes('Land Activities') || landSelectedInCustom;
    hikeShapesRef.current.forEach(h => {
      try { if (h && h.shape && h.shape.setMap) h.shape.setMap(show ? mapInstance.current : null); } catch (e) {}
    });
  } catch (e) {}
}, [activeGroups, customFilterActive, customSelectedMarkers]);
// Hide/show hike polygons depending on map extent (hide when view exceeds 50km)
React.useEffect(() => {
  if (!mapInstance.current || !window.google || !window.google.maps) return;
  const haversine = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371000; // metres
    const toRad = (v: number) => v * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getBoundsDiagonalMeters = () => {
    try {
      const bounds = mapInstance.current.getBounds();
      if (!bounds) return Infinity;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      return haversine(ne.lat(), ne.lng(), sw.lat(), sw.lng());
    } catch (e) {
      return Infinity;
    }
  };

  const updateHikeVisibility = () => {
    try {
      const landNames = bajaMarkers.filter(m => m.group === 'Land Activities').map(m => m.name);
      const landSelectedInCustom = customFilterActive && landNames.some(n => customSelectedMarkers.includes(n));
      const groupShow = activeGroups.includes('Land Activities') || landSelectedInCustom;
      // hide polygons when diagonal of current bounds exceeds 50000 meters
      const withinZoom = getBoundsDiagonalMeters() <= 50000;
      const finalShow = groupShow && withinZoom;
      hikeShapesRef.current.forEach(h => {
        try { if (h && h.shape && h.shape.setMap) h.shape.setMap(finalShow ? mapInstance.current : null); } catch (e) {}
      });
    } catch (e) {}
  };

  // call initially
  updateHikeVisibility();
  // update on map idle (after zoom/pan)
  const listener: any = mapInstance.current.addListener && mapInstance.current.addListener('idle', () => updateHikeVisibility());
  return () => { try { if (listener && listener.remove) listener.remove(); } catch (e) {} };
}, [activeGroups, customFilterActive, customSelectedMarkers]);
  // --- Distance Measurement Feature: Draw line and calculate distance ---
  useEffect(() => {
    if (!mapInstance.current || !window.google || !window.google.maps) return;
    // Remove previous start/end markers and segment windows if any
    if (measureStartMarker) {
      try { measureStartMarker.setMap(null); } catch (e) {}
      setMeasureStartMarker(null);
    }
    if (measureEndMarker) {
      try { measureEndMarker.setMap(null); } catch (e) {}
      setMeasureEndMarker(null);
    }
    // clear any existing per-segment info windows
  try { measureSegmentWindowsRef.current.forEach(w => { try { if (w && w.close) w.close(); else if (w && w.setMap) w.setMap(null); } catch (e) {} }); measureSegmentWindowsRef.current = []; } catch (e) {}
    // Add marker for the first measurement point if present
    if (measurePoints.length === 1) {
      // @ts-ignore
      const { Marker } = window.google.maps;
      const pt = measurePoints[0];
      // don't show the small green start marker if this point matches a known baja marker
      const tol = 1e-5;
      const matchesBaja = bajaMarkers.some(m => Math.abs(m.lat - pt.lat) < tol && Math.abs(m.lng - pt.lng) < tol);
      // or if it matches the persistent location marker (if present)
      let matchesLocation = false;
      try {
        if (locationMarkerRef.current && locationMarkerRef.current.getPosition) {
          const pos = locationMarkerRef.current.getPosition();
          if (pos) {
            const lat = pos.lat();
            const lng = pos.lng();
            if (Math.abs(lat - pt.lat) < tol && Math.abs(lng - pt.lng) < tol) matchesLocation = true;
          }
        }
      } catch (e) {}
      // For radius mode we always want a visible center handle so the circle can be moved,
      // even if the clicked point matches a known marker or the persistent location.
      if (measurementModeRef.current === 'radius' || (!matchesBaja && !matchesLocation)) {
        const marker = new Marker({
          map: mapInstance.current,
          position: measurePoints[0],
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            // larger and thicker handle in radius mode for visibility on mobile
            scale: (measurementModeRef.current === 'radius') ? 12 : 6,
            fillColor: '#388e3c',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: (measurementModeRef.current === 'radius') ? 3 : 2,
          },
          // keep marker above the circle and other overlays
          zIndex: (measurementModeRef.current === 'radius') ? 1000000 : 99999,
          // This marker is not draggable. Radius movement is handled by a dedicated radius center marker
          // so we avoid overwriting distance measurement state (measurePoints).
          draggable: false,
          clickable: false,
        });
        // keep behavior consistent but avoid attaching drag handlers here that alter measurePoints.
        // Radius center dragging is handled by the dedicated radius center marker.
        setMeasureStartMarker(marker);
      }
    }
    // create an interactive end marker for the last point (if any)
    if (measurePoints.length >= 1) {
      try {
        const lastPt = measurePoints[measurePoints.length - 1];
        const { Marker } = window.google.maps;
        const endMarker = new Marker({
          map: mapInstance.current,
          position: lastPt,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: '#1976d2',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          zIndex: 99999,
          title: 'Measurement end (click to continue)'
        });
        // clicking the end marker will re-enable measuring so user can add another point
        try {
          endMarker.addListener('click', (e: any) => {
            setMeasuring(true);
          });
        } catch (e) {}
        setMeasureEndMarker(endMarker);
      } catch (e) {}
    }
    // Remove previous line and label
    if (measureLine) {
      measureLine.setMap(null);
      setMeasureLine(null);
    }
    if (window.measureInfoWindow) {
      window.measureInfoWindow.close();
      window.measureInfoWindow = null;
    }
  if (measurePoints.length >= 2) {
      // @ts-ignore
      const { Polyline, InfoWindow } = window.google.maps;
      // Create a grey dashed polyline using repeated small segment icons
      const line = new Polyline({
        map: mapInstance.current,
        path: measurePoints,
        strokeColor: '#777',
        strokeOpacity: 0,
        strokeWeight: 4,
        zIndex: 9999,
        icons: [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            strokeWeight: 3,
            scale: 4,
            strokeColor: '#777'
          },
          offset: '0',
          repeat: '14px'
        }]
      });
      // Click listener: append clicked location on line to extend measurement
      line.addListener('click', (e: any) => {
        if (!measuringRef.current) return;
        const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setMeasurePoints(prev => {
          const tol = 1e-6;
          if (!prev || prev.length === 0) return [latLng];
          const last = prev[prev.length - 1];
          if (Math.abs(last.lat - latLng.lat) < tol && Math.abs(last.lng - latLng.lng) < tol) return prev;
          return [...prev, latLng];
        });
      });
  setMeasureLine(line);
      // Calculate cumulative distance along the polyline
      // @ts-ignore
      if (window.google.maps.geometry && window.google.maps.geometry.spherical) {
        let total = 0;
        // optionally create per-segment labels
        for (let i = 1; i < measurePoints.length; i++) {
          // @ts-ignore
          const seg = window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(measurePoints[i - 1].lat, measurePoints[i - 1].lng),
            new window.google.maps.LatLng(measurePoints[i].lat, measurePoints[i].lng)
          );
          total += seg;
          if (showSegmentLabels) {
            try {
              const midLat = (measurePoints[i - 1].lat + measurePoints[i].lat) / 2;
              const midLng = (measurePoints[i - 1].lng + measurePoints[i].lng) / 2;
              const label = seg > 1852 ? `${(seg / 1852).toFixed(2)} NM` : `${seg.toFixed(1)} m`;
              // create a small inline marker element that sits on the line
              try {
                // @ts-ignore
                const { AdvancedMarkerElement } = window.google.maps.marker;
                const container = document.createElement('div');
                container.style.padding = '4px 8px';
                container.style.background = 'rgba(255,255,255,0.95)';
                container.style.border = '1px solid rgba(0,0,0,0.08)';
                container.style.borderRadius = '12px';
                container.style.fontSize = '12px';
                container.style.fontWeight = '600';
                container.style.color = '#333';
                container.style.boxShadow = '0 1px 6px rgba(0,0,0,0.08)';
                container.style.pointerEvents = 'none';
                container.innerText = label;
                const adv = new AdvancedMarkerElement({
                  map: mapInstance.current,
                  position: { lat: midLat, lng: midLng },
                  title: label,
                  content: container,
                  zIndex: 99998,
                });
                measureSegmentWindowsRef.current.push(adv);
              } catch (e) {
                // Fallback to InfoWindow if AdvancedMarkerElement not available
                const win = new InfoWindow({
                  content: `<div style='font-size:13px;font-weight:600;color:#555;background:#fff;padding:3px 8px;border-radius:6px;border:1.5px solid #eee;white-space:nowrap;'>${label}</div>`,
                  position: { lat: midLat, lng: midLng },
                  pixelOffset: new window.google.maps.Size(0, -12),
                  disableAutoPan: true,
                });
                win.open({ map: mapInstance.current });
                measureSegmentWindowsRef.current.push(win);
              }
            } catch (e) {}
          }
        }
  // place overall label near the last measured segment (between last two points)
  const lastIdx = measurePoints.length - 1;
  const prevIdx = Math.max(0, lastIdx - 1);
  const midLat = (measurePoints[prevIdx].lat + measurePoints[lastIdx].lat) / 2;
  const midLng = (measurePoints[prevIdx].lng + measurePoints[lastIdx].lng) / 2;
        const label = total > 1852 ? `${(total / 1852).toFixed(2)} NM` : `${total.toFixed(1)} m`;
        try {
          const infoWindow = new InfoWindow({
            content: `<div style='font-size:15px;font-weight:600;color:#555;background:#fff;padding:4px 10px;border-radius:6px;border:1.5px solid #ddd;box-shadow:0 2px 8px rgba(0,0,0,0.06);white-space:nowrap;'>${label}</div>` +
              `<style>.gm-ui-hover-effect { display: none !important; }</style>`,
            position: { lat: midLat, lng: midLng },
            pixelOffset: new window.google.maps.Size(0, -18),
            disableAutoPan: true,
          });
          infoWindow.open({ map: mapInstance.current });
          window.measureInfoWindow = infoWindow;
        } catch (e) {}
      }
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurePoints, mapInstance.current, showSegmentLabels]);

  // Reset measurement when disabling measuring mode
  useEffect(() => {
    if (!measuring) {
      // always clear measurement when measuring is turned off
      setMeasurePoints([]);
      if (measureLine) {
        measureLine.setMap(null);
        setMeasureLine(null);
      }
      if (measureStartMarker) {
        measureStartMarker.setMap(null);
        setMeasureStartMarker(null);
      }
      if (measureEndMarker) {
        measureEndMarker.setMap(null);
        setMeasureEndMarker(null);
      }
    // clear radius circle if present
    try { clearRadius(); } catch (e) {}
      if (window.measureInfoWindow) {
        window.measureInfoWindow.close();
        window.measureInfoWindow = null;
      }
      try { measureSegmentWindowsRef.current.forEach(w => { try { if (w && w.close) w.close(); else if (w && w.setMap) w.setMap(null); } catch (e) {} }); measureSegmentWindowsRef.current = []; } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuring]);

  // Ensure start marker draggable state follows measurement mode (useful when switching to radius on mobile)
  useEffect(() => {
    try {
      if (measureStartMarker && measureStartMarker.setDraggable) {
        measureStartMarker.setDraggable(measurementMode === 'radius');
      }
      // if in radius mode and there is a start marker but no circle, create one quickly to keep visuals consistent
      if (measurementMode === 'radius' && measureStartMarker && !radiusCircleRef.current) {
        try {
          // create a circle centered at start marker with current radius
          if (mapInstance.current && window.google && window.google.maps) {
            const pos = measureStartMarker.getPosition();
            if (pos) {
              const center = { lat: pos.lat(), lng: pos.lng() };
              const { Circle } = window.google.maps;
              const circle = new Circle({ map: mapInstance.current, center, radius: Math.max(0, Number(radiusMetersRef.current) || 0), strokeColor: '#1976d2', strokeOpacity: 0.6, strokeWeight: 2, fillColor: '#1976d2', fillOpacity: 0.12, zIndex: 99990 });
              radiusCircleRef.current = circle;
            }
          }
        } catch (e) {}
      }
    } catch (e) {}
  }, [measurementMode, measureStartMarker]);

  // Marker rendering and filtering (runs on activeGroups, bajaMarkers, or mapInstance change)
  React.useEffect(() => {
    if (!mapInstance.current || !window.google || !window.google.maps) return;

    // Clear previous clusterer if present
    try {
      if (clustererRef.current && clustererRef.current.clearMarkers) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    } catch (e) {}

    // Remove old markers from map
    markersRef.current.forEach((m) => { try { m.setMap(null); } catch (e) {} });
    markersRef.current = [];

    // Select markers to show. Always include agent markers (they should
    // always be visible regardless of group filters or custom selection).
    const isAgentMarker = (m: any) => !!(m.group && String(m.group).toLowerCase().includes('agent'));
    const markersToShow = customFilterActive
      ? bajaMarkers.filter(m => customSelectedMarkers.includes(m.name) || isAgentMarker(m))
      : bajaMarkers.filter(m => activeGroups.includes(m.group) || isAgentMarker(m));

    // Create google.maps.Marker instances. Tag agent markers so we can
    // keep them unclustered and always visible.
    const gmarkers = markersToShow.map((marker) => {
      const isAgent = isAgentMarker(marker);
      const mk = new window.google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        title: marker.name,
        icon: {
          url: marker.icon,
          scaledSize: new window.google.maps.Size(32, 32),
        },
        // Agent markers should render above regular markers but below
        // cluster icons so cluster clicks are handled first.
        zIndex: isAgent ? 100 : 0,
        optimized: isAgent ? false : true,
      });
      // mark the marker instance for later splitting
      (mk as any).__isAgent = isAgent;

      mk.addListener('click', (e: any) => {
        if (measuringRef.current) {
          setMeasurePoints(prev => {
            const pt = { lat: marker.lat, lng: marker.lng };
            const tol = 1e-6;
            if (!prev || prev.length === 0) return [pt];
            const last = prev[prev.length - 1];
            if (Math.abs(last.lat - pt.lat) < tol && Math.abs(last.lng - pt.lng) < tol) return prev;
            return [...prev, pt];
          });
          return;
        }
        setPopup({ title: marker.name, images: marker.images && Array.isArray(marker.images) ? marker.images : [], description: marker.description });
        setImgIdx(0);
        setLightboxOpen(false);
        justOpenedRef.current = true;
        setTimeout(() => { justOpenedRef.current = false; }, 350);
      });

      return mk;
    });

    // Create clusterer and attach markers. Agent markers are added directly
    // to the map and not passed into the clusterer so they remain unclustered.
    try {
      const agentMarkers = gmarkers.filter(m => (m as any).__isAgent);
      const nonAgentMarkers = gmarkers.filter(m => !(m as any).__isAgent);
      if (clusteringEnabled) {
        // Use a custom renderer to draw a single-ring SVG cluster icon
        const clusterer = new MarkerClusterer({
          map: mapInstance.current,
          markers: nonAgentMarkers,
          renderer: {
            render: ({ count, position }: any) => {
              // Fixed size to match individual marker icon (32x32)
              const size = 32;
              const stroke = 3;
              const color = '#1976d2';
              const textColor = '#ffffff';
              const fontSize = 12;
              const svg = `<?xml version="1.0" encoding="UTF-8"?>\n` +
                `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>` +
                `<circle cx='${size/2}' cy='${size/2}' r='${(size/2) - stroke/2}' fill='${color}' fill-opacity='0.36' stroke='${color}' stroke-width='${stroke}' />` +
                `<text x='50%' y='50%' text-anchor='middle' dominant-baseline='central' font-family='Arial, Helvetica, sans-serif' font-size='${fontSize}' fill='${textColor}' font-weight='600'>${count}</text>` +
                `</svg>`;
              const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
              const marker = new window.google.maps.Marker({
                position,
                icon: {
                  url,
                  scaledSize: new window.google.maps.Size(size, size),
                  anchor: new window.google.maps.Point(size / 2, size / 2),
                },
                zIndex: 999999,
                clickable: true,
              });
              return marker;
            }
          }
        });
        clustererRef.current = clusterer;
        // ensure agent markers are visible individually
        agentMarkers.forEach(m => { try { m.setMap(mapInstance.current); } catch (e) {} });
      } else {
        // simply add all markers to the map without clustering
        gmarkers.forEach(m => { try { m.setMap(mapInstance.current); } catch (e) {} });
      }
    } catch (e) {
      // fallback: just add markers to map
      gmarkers.forEach(m => { try { m.setMap(mapInstance.current); } catch (e) {} });
    }

    markersRef.current = gmarkers;

    // Cleanup on effect re-run / unmount
    return () => {
      try { if (clustererRef.current && clustererRef.current.clearMarkers) clustererRef.current.clearMarkers(); } catch (e) {}
      markersRef.current.forEach((m) => { try { m.setMap(null); } catch (e) {} });
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroups, bajaMarkers, mapInstance.current, measurePoints, customFilterActive, customSelectedMarkers, clusteringEnabled]);

  // Popup open helper
  function openPopup(p: { title: string; images: string[]; description: string }) {
    if (!p.title && (!p.description || p.description.replace(/<[^>]+>/g, '').trim() === '') && (!p.images || p.images.length === 0)) return;
    setPopup(p);
    setImgIdx(0);
    setLightboxOpen(false);
    justOpenedRef.current = true;
    setTimeout(() => { justOpenedRef.current = false; }, 300);
  }

  // Toggle favorite for a marker: add/remove from custom selection (do NOT apply the custom filter)
  const toggleFavorite = (name: string) => {
    try {
      const isFav = customSelectedMarkers.includes(name);
      if (isFav) {
        setCustomSelectedMarkers(prev => prev.filter(x => x !== name));
        return;
      }
      // Only update the custom selection list; do NOT enable the custom filter or change active groups
      setCustomSelectedMarkers(prev => prev.includes(name) ? prev : [...prev, name]);
    } catch (e) {}
  };

  // Handler to get user's current location and show on map
  const handleLocateClick = () => {
    // Toggle off: remove marker and circle
    if (locationActive) {
      setLocationActive(false);
      try {
        if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; }
        if (locationMarkerRef.current) {
          locationMarkerRef.current.setMap(null);
          locationMarkerRef.current = null;
        }
        if (locationCircleRef.current) {
          locationCircleRef.current.setMap(null);
          locationCircleRef.current = null;
        }
      } catch (e) { console.error('Error clearing location', e); }
      return;
    }
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const accuracy = pos.coords.accuracy || 0;
        try {
          // clear any previous (remove listener first)
          try { if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; } } catch (e) {}
          if (locationMarkerRef.current) {
            locationMarkerRef.current.setMap(null);
            locationMarkerRef.current = null;
          }
          if (locationCircleRef.current) {
            locationCircleRef.current.setMap(null);
            locationCircleRef.current = null;
          }
          if (mapInstance.current && window.google && window.google.maps) {
            mapInstance.current.panTo({ lat, lng });
            try { mapInstance.current.setZoom(15); } catch (e) {}
            // show persistent marker for the user's location
            locationMarkerRef.current = new window.google.maps.Marker({
              map: mapInstance.current,
              position: { lat, lng },
              title: 'Your location',
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 6,
                fillColor: '#1976d2',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
              },
            });
            // attach click listener to behave like other markers when measuring
            try {
              if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; }
              locationMarkerListenerRef.current = locationMarkerRef.current.addListener('click', (e: any) => {
                if (!measuringRef.current) return;
                const pt = { lat, lng };
                setMeasurePoints(prev => {
                  const tol = 1e-6;
                  if (!prev || prev.length === 0) return [pt];
                  const last = prev[prev.length - 1];
                  if (Math.abs(last.lat - pt.lat) < tol && Math.abs(last.lng - pt.lng) < tol) return prev;
                  return [...prev, pt];
                });
              });
            } catch (e) {}
            // add an accuracy / ripple circle
            locationCircleRef.current = new window.google.maps.Circle({
              map: mapInstance.current,
              center: { lat, lng },
              radius: Math.max(accuracy, 20),
              strokeColor: '#1976d2',
              strokeOpacity: 0.25,
              strokeWeight: 2,
              fillColor: '#1976d2',
              fillOpacity: 0.06,
              zIndex: 99990,
            });
            // If measuring mode is active, append current location as measurement point (mirrors marker click behavior)
            if (measuringRef.current) {
              setMeasurePoints(prev => {
                const pt = { lat, lng };
                const tol = 1e-6;
                if (!prev || prev.length === 0) return [pt];
                const last = prev[prev.length - 1];
                if (Math.abs(last.lat - pt.lat) < tol && Math.abs(last.lng - pt.lng) < tol) return prev;
                return [...prev, pt];
              });
            }
            setLocationActive(true);
          }
        } catch (e) {
          console.error('Error showing location on map', e);
        }
        setLocating(false);
      },
      (err) => {
        console.warn('Geolocation error', err);
        if (err && err.code === 1) {
          alert('Permission denied. Please allow location access in your browser.');
        } else {
          alert('Could not determine your location.');
        }
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Reset desktop zoom when opening/closing lightbox or switching images
  useEffect(() => {
    if (lightboxOpen) {
      setDesktopZoomed(false);
    }
  }, [lightboxOpen, imgIdx]);

  // Center image in viewport when zooming out (desktop)
  useEffect(() => {
    if (!isTouchDevice && !desktopZoomed && lightboxOpen && lightboxContainerRef.current) {
      const container = lightboxContainerRef.current;
      container.scrollTo({
        left: (container.scrollWidth - container.clientWidth) / 2,
        top: (container.scrollHeight - container.clientHeight) / 2,
        behavior: 'smooth',
      });
    }
  }, [desktopZoomed, lightboxOpen, isTouchDevice]);

  // Desktop pan state for enlarged image
  const [desktopPan, setDesktopPan] = useState({ x: 0, y: 0 });
  const [desktopDragging, setDesktopDragging] = useState(false);
  const desktopDragStart = useRef<{ x: number; y: number } | null>(null);
  // Track if a drag occurred to prevent zoom toggle on drag
  const desktopDragMoved = useRef(false);
  // Use a movement threshold to distinguish click from drag
  const desktopDragStartPos = useRef<{ x: number; y: number } | null>(null);

  // Reset pan when closing or zooming out
  useEffect(() => {
    if (!desktopZoomed) setDesktopPan({ x: 0, y: 0 });
  }, [desktopZoomed, lightboxOpen]);

  // Desktop pan handlers
  // Desktop drag-to-pan and click-to-zoom logic
  function handleDesktopMouseDown(e: React.MouseEvent<HTMLImageElement>) {
    if (!desktopZoomed) {
      // Not zoomed: prepare for possible click-to-zoom
      desktopDragMoved.current = false;
      desktopDragStartPos.current = { x: e.clientX, y: e.clientY };
      return;
    }
    // Zoomed: start dragging
    setDesktopDragging(true);
    desktopDragStart.current = { x: e.clientX - desktopPan.x, y: e.clientY - desktopPan.y };
    desktopDragMoved.current = false;
    desktopDragStartPos.current = { x: e.clientX, y: e.clientY };
  }

  function handleDesktopMouseMove(e: React.MouseEvent<HTMLImageElement>) {
    if (!desktopZoomed || !desktopDragging) return;
    setDesktopPan({
      x: e.clientX - (desktopDragStart.current?.x ?? 0),
      y: e.clientY - (desktopDragStart.current?.y ?? 0),
    });
    if (desktopDragStartPos.current) {
      const dx = e.clientX - desktopDragStartPos.current.x;
      const dy = e.clientY - desktopDragStartPos.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        desktopDragMoved.current = true;
      }
    }
  }

  function handleDesktopMouseUp(e: React.MouseEvent<HTMLImageElement>) {
    if (!desktopZoomed) {
      // Not zoomed: treat as click if not a drag
      if (desktopDragStartPos.current) {
        const dx = e.clientX - desktopDragStartPos.current.x;
        const dy = e.clientY - desktopDragStartPos.current.y;
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
          setDesktopZoomed(true);
        }
      }
      desktopDragStartPos.current = null;
      return;
    }
    // Zoomed: if dragged, just stop dragging; if not dragged, zoom out
    setDesktopDragging(false);
    if (desktopDragMoved.current) {
      desktopDragMoved.current = false;
      desktopDragStartPos.current = null;
      return;
    }
    setDesktopZoomed(false);
    desktopDragMoved.current = false;
    desktopDragStartPos.current = null;
  }

  // --- Backend login/session logic ---
  // Place this block after all useState declarations for password, passwordValid, passwordError, sessionToken, and activeGroups
  // (Removed duplicate password/session init effect, now handled above)

  React.useEffect(() => {
    if (!passwordValid || !password || !sessionToken) return;
    const interval = setInterval(() => {
  fetch(`${API_BASE_URL}/api/check-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, sessionToken }),
      })
        .then(async res => {
          let data;
          try {
            data = await res.json();
          } catch {
            data = {};
          }
          if (!data.valid) {
            setPasswordValid(false);
            setSessionToken(null);
            setPasswordError(data.message || 'You have been logged out because this password was used on another device.');
            localStorage.removeItem('baja_password');
            localStorage.removeItem('baja_sessionToken');
          }
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [passwordValid, password, sessionToken]);

// Removed duplicate handleLogin definition

  // Only set all groups as active once after successful login
  const groupsInitialized = React.useRef(false);
  useEffect(() => {
    const uniqueGroups = Array.from(new Set(bajaMarkers.map(m => m.group).filter(Boolean)))
      .filter(g => !(g && g.toLowerCase().includes('agent')));
    if (!passwordValid) {
      if (activeGroups.length !== 0) setActiveGroups([]);
      groupsInitialized.current = false;
      return;
    }
    // Set all groups as active only on first login after login
    if (!groupsInitialized.current && uniqueGroups.length > 0 && activeGroups.length === 0) {
      setActiveGroups(uniqueGroups);
      groupsInitialized.current = true;
    }
  }, [passwordValid, activeGroups.length]);

  // Load saved custom selections from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('baja_custom_selected_markers');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setCustomSelectedMarkers(parsed as string[]);
      }
      const rawActive = localStorage.getItem('baja_custom_filter_active');
      if (rawActive) setCustomFilterActive(rawActive === '1');
    } catch (e) {}
  }, []);

  // Persist custom selections when they change
  useEffect(() => {
    try {
      localStorage.setItem('baja_custom_selected_markers', JSON.stringify(customSelectedMarkers));
      localStorage.setItem('baja_custom_filter_active', customFilterActive ? '1' : '0');
    } catch (e) {}
  }, [customSelectedMarkers, customFilterActive]);

  const loadingOverlay = sessionLoading ? (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', zIndex: 20000, position: 'fixed', top: 0, left: 0 }}>
      <div style={{ fontSize: 22, color: '#1976d2' }}>Loading...</div>
    </div>
  ) : null;
  // When Street View is active we will hide app UI overlays via inline styles
  const uiHiddenStyle = streetViewActive ? { display: 'none' as const } : undefined;

  // Hide any DOM nodes (including ones rendered outside React roots) marked with
  // data-hide-in-streetview by toggling a CSS class. Using a class avoids
  // racing with React inline style updates and ensures a reliable restore.
  React.useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-hide-in-streetview]'));
    if (streetViewActive) {
      els.forEach(el => el.classList.add('app-hide-in-streetview'));
      // mark body with a small, app-scoped class so CSS can target app elements
      // without touching Google Maps internals
      try { document.body.classList.add('app-streetview-active'); } catch (e) {}
    } else {
      els.forEach(el => el.classList.remove('app-hide-in-streetview'));
      try { document.body.classList.remove('app-streetview-active'); } catch (e) {}
    }
    // cleanup: ensure class removed on unmount
    return () => { els.forEach(el => el.classList.remove('app-hide-in-streetview')); };
  }, [streetViewActive]);

  return (
    <>
  {loadingOverlay}
  {/* Show the payment result modal if Stripe redirected here */}
  {__isPaymentResultPath && <PaymentResult result={__paymentResultType as 'success' | 'cancel'} />}
      {/* ...existing code... */}
      {/* Desktop-only banner in top left over map (no map type control here) */}
      {passwordValid && isDesktop && (
        <div className="desktop-banner-top-left" data-hide-in-streetview style={uiHiddenStyle}>
          <img
            src="/images/logos/banner_seaofcortez.svg"
            alt="Sea of Cortez Banner"
            style={{ width: 'clamp(220px, 48vw, 1000px)', height: 'auto', maxWidth: '100vw', display: 'block' }}
          />
        </div>
      )}
      {/* Password login popup */}
      {!passwordValid && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20000,
            overflow: 'hidden',
            ...(uiHiddenStyle || {}),
            ...(uiHiddenStyle || {}),
          }}
          data-hide-in-streetview
        >
          <div
            style={{
              background: '#fff',
              padding: isMobile ? 24 : 48,
              borderRadius: 16,
              boxShadow: '0 2px 24px rgba(0,0,0,0.22)',
              minWidth: isMobile ? 320 : 440,
              maxWidth: isMobile ? 400 : 540,
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              transition: 'all 0.2s',
            }}
          >
            {/* Logo inside popup, low saturation */}
            <img
              src={logoSeaOfCortez}
              alt="Sea of Cortez Logo"
              style={{
                width: 90,
                height: 90,
                objectFit: 'contain',
                marginBottom: 10,
                userSelect: 'none',
                pointerEvents: 'none',
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 8 }}>
              <button
                style={{ background: 'none', border: 'none', fontSize: 22, color: '#1976d2', cursor: 'pointer', opacity: featurePoints.length <= 1 ? 0.3 : 1, padding: '0 8px', display: 'flex', alignItems: 'center' }}
                onClick={() => setFeatureIdx(idx => (idx - 1 + featurePoints.length) % featurePoints.length)}
                aria-label="Previous feature"
                tabIndex={0}
                disabled={featurePoints.length <= 1}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <h2 style={{ margin: 0, fontSize: 26 }}>Sea of Cortez Explorer</h2>
              <button
                style={{ background: 'none', border: 'none', fontSize: 22, color: '#1976d2', cursor: 'pointer', opacity: featurePoints.length <= 1 ? 0.3 : 1, padding: '0 8px', display: 'flex', alignItems: 'center' }}
                onClick={() => setFeatureIdx(idx => (idx + 1) % featurePoints.length)}
                aria-label="Next feature"
                tabIndex={0}
                disabled={featurePoints.length <= 1}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
              </button>
            </div>
            <div
              style={{ margin: '18px 0 24px 0', minHeight: 110, position: 'relative', userSelect: 'none' }}
              onTouchStart={e => { if (e.touches.length === 1) carouselTouchStart.current = e.touches[0].clientX; }}
              onTouchMove={e => { if (e.touches.length === 1) carouselTouchEnd.current = e.touches[0].clientX; }}
              onTouchEnd={() => {
                if (carouselTouchStart.current !== null && carouselTouchEnd.current !== null) {
                  const dx = carouselTouchEnd.current - carouselTouchStart.current;
                  if (Math.abs(dx) > 40) {
                    if (dx < 0) setFeatureIdx(idx => (idx + 1) % featurePoints.length);
                    else setFeatureIdx(idx => (idx - 1 + featurePoints.length) % featurePoints.length);
                  }
                }
                carouselTouchStart.current = null;
                carouselTouchEnd.current = null;
              }}
            >
              <div style={{ fontWeight: 500, fontSize: 16, color: '#1976d2', minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'left', padding: '0 8px', flexDirection: 'column' }}>
                <>
                  {featurePoints[featureIdx].image && (
                    <img
                      src={featurePoints[featureIdx].image}
                      alt="Example marker"
                      style={{
                        width: isMobile
                          ? (carouselImgEnlarged ? 360 : 220)
                          : (carouselImgEnlarged ? 480 : 320),
                        height: isMobile
                          ? (carouselImgEnlarged ? 230 : 140)
                          : (carouselImgEnlarged ? 310 : 190),
                        objectFit: 'cover',
                        borderRadius: 12,
                        marginBottom: 14,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.16)',
                        cursor: 'pointer',
                        transition: 'width 0.18s, height 0.18s, box-shadow 0.18s',
                        zIndex: 10,
                      }}
                      onClick={e => setCarouselImgEnlarged(v => !v)}
                    />
                  )}
                  {featurePoints[featureIdx].text || featurePoints[featureIdx]}
                </>
              </div>
              {/* Removed carousel navigation < 1/4 > and its buttons as requested */}
            </div>
            <p style={{ margin: '18px 0 8px 0' }}>Please enter the password to view the map markers.</p>
            <input
              key={password}
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPasswordError(""); }}
              style={{ fontSize: 18, padding: 8, width: '60%', marginBottom: 12, borderRadius: 4, border: '1px solid #ccc', marginRight: 8 }}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleLogin();
                }
              }}
            />
            <button
              style={{ fontSize: 18, padding: '8px 16px', borderRadius: 4, border: '1px solid #1976d2', background: '#1976d2', color: '#fff', cursor: 'pointer', marginBottom: 12 }}
              onClick={handleLogin}
            >Login</button>
            <button
              style={{ fontSize: 18, padding: '8px 16px', borderRadius: 4, border: '1px solid #888', background: '#fff', color: '#1976d2', cursor: 'pointer', marginBottom: 12, marginLeft: 8 }}
              onClick={() => { setRequestAccessOpen(true); setAccessStep('select'); setAccessPlan(null); setAccessEmail(''); setAccessMessage(''); }}
            >Request Access</button>
      {/* Request Access Popup (modernized) */}
      {requestAccessOpen && (
        <div className="access-modal-overlay">
          <div className="access-modal-card" role="dialog" aria-modal="true" aria-labelledby="access-title">
            <div className="access-modal-header">
              <h3 id="access-title" className="access-modal-title">Request Access</h3>
              <button className="access-modal-close" aria-label="Close" onClick={() => setRequestAccessOpen(false)}>✕</button>
            </div>
            <p className="access-description">
              Purchasing an access token grants you time-limited access to the website and its features for the selected duration.
            </p>
            <p className="access-description">
              Checkout will redirect you to Stripe to complete payment. After payment is successfully processed, a login token will be sent to the email address you provide.
            </p>
            <div className="plans-row">
              <div
                className={`plan-card ${accessPlan === '3months' ? 'selected' : ''}`}
                onClick={() => setAccessPlan('3months')}
                role="button"
                tabIndex={0}
              >
                <div className="plan-title">3 months</div>
                <div className="plan-price">$75</div>
                <div className="plan-sub">Access valid for 3 months from first use</div>
              </div>
              <div
                className={`plan-card ${accessPlan === '1year' ? 'selected' : ''}`}
                onClick={() => setAccessPlan('1year')}
                role="button"
                tabIndex={0}
              >
                <div className="plan-title">1 year</div>
                <div className="plan-price">$250</div>
                <div className="plan-sub">Access valid for 1 year from first use</div>
              </div>
            </div>
            {accessStep === 'select' && (
              <div className="access-actions">
                <button
                  onClick={() => { if (!accessPlan) { setAccessMessage('Please select a plan first.'); return; } setAccessStep('email'); setAccessMessage(''); }}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #1976d2', background: '#1976d2', color: '#fff', cursor: 'pointer' }}
                >Proceed to Checkout</button>
                <button
                  onClick={() => setRequestAccessOpen(false)}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #d6dceb', background: '#fff', color: '#34445a', cursor: 'pointer' }}
                >Cancel</button>
              </div>
            )}
            {accessStep === 'email' && (
              <>
                <p style={{ marginTop: 8, marginBottom: 8, fontSize: 14, color: '#23314a' }}>Enter the email address where you'd like to receive the login token after payment completion for <strong>{accessPlan === '3months' ? '3 months — $75' : '1 year — $250'}</strong>.</p>
                <input
                  className="access-input"
                  type="email"
                  placeholder="name@example.com"
                  value={accessEmail}
                  onChange={e => setAccessEmail(e.target.value)}
                />
                <div className="access-actions">
                  <button
                    onClick={async () => {
                      if (!accessEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(accessEmail)) { setAccessMessage('Please enter a valid email address.'); return; }
                      setAccessLoading(true);
                      setAccessMessage('');
                      try {
                        // Note: Stripe redirection integration is expected here in production.
                        // For now this request will create the password and email it to the provided address (used for testing/local).
                        // Create Stripe Checkout Session and redirect to Stripe Checkout
                        const res = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: accessEmail, plan: accessPlan }),
                        });
                        const data = await res.json();
                        if (res.ok && data && data.url) {
                          // Redirect the browser to the Stripe Checkout page
                          window.location.href = data.url;
                        } else {
                          setAccessMessage(data.message || 'Could not create checkout session.');
                        }
                      } catch (err) {
                        setAccessMessage('Server error. Please try again later.');
                      }
                      setAccessLoading(false);
                    }}
                    style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #1976d2', background: '#1976d2', color: '#fff', cursor: 'pointer' }}
                    disabled={accessLoading}
                  >{accessLoading ? 'Processing...' : 'Proceed to Stripe'}</button>
                  <button
                    onClick={() => { setAccessStep('select'); setAccessMessage(''); }}
                    style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #d6dceb', background: '#fff', color: '#34445a', cursor: 'pointer' }}
                    disabled={accessLoading}
                  >Back</button>
                </div>
                <div className="access-note">You'll be redirected to Stripe to complete payment. After a successful payment, a login token will be emailed to you.</div>
              </>
            )}
            {accessStep === 'done' && (
              <>
                <p className="access-message">{accessMessage || 'Request processed. Check your email for the login token.'}</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button onClick={() => setRequestAccessOpen(false)} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #1976d2', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>Close</button>
                </div>
              </>
            )}
            {accessMessage && accessStep !== 'done' && <div className="access-message" style={{ color: accessMessage.startsWith('Please') ? '#a46d00' : '#19692b' }}>{accessMessage}</div>}
          </div>
        </div>
      )}
            <br />
            {passwordError && <div style={{ color: 'red', marginTop: 8 }}>{passwordError}</div>}
            {/* Legal notice link inside the popup */}
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 15,
                  padding: 0,
                }}
                onClick={() => setLegalOpen(true)}
              >
                Legal Notice / Privacy Policy / Terms
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Marker search bar (only show when logged in) */}
      {passwordValid && ((!lightboxOpen && !popup) || document.fullscreenElement) && (
        <div
          className="marker-search-bar"
          ref={searchBarContainerRef}
          style={{
            position: 'absolute',
            top: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 520,
            maxWidth: '90vw',
            boxSizing: 'border-box',
            zIndex: 10001,
            background: '#fff',
            border: '1px solid #aaa',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            padding: 0,
            borderRadius: 10,
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flexDirection: 'column',
            ...(uiHiddenStyle || {}),
          }}
          data-hide-in-streetview
  >
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative', padding: 6 }}>
            {/* Search icon (lupen) */}
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 8,
                marginRight: 6,
                color: '#888',
                fontSize: 22,
                cursor: 'pointer',
                userSelect: 'none',
              }}
                onClick={() => {
                setSearchActive(true);
                // focus the actual input so desktop behaves like mobile tap
                try { searchInputRef.current && searchInputRef.current.focus(); } catch (e) {}
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            </span>


            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', position: 'relative' }}>
              <input
                ref={el => searchInputRef.current = el}
                type="text"
                placeholder={searchMode === 'markers' ? 'Search marker' : 'Search location'}
                value={search}
                onChange={e => {
                  const value = e.target.value;
                  setSearch(value);
                  setSearchActive(true);
                  // typing cancels the forced full-list view
                  setShowAllMarkersList(false);
                  if (value.trim() === "") {
                    setShowSearchButtons(true);
                  } else {
                    setShowSearchButtons(false);
                  }
                }}
                onFocus={() => {
                  setSearchActive(true);
                  setShowSearchButtons(true);
                }}
                style={{
                  width: '100%',
                  padding: '10px 10px 10px 0',
                  fontSize: 18,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  pointerEvents: 'auto',
                }}
              />
              {showSearchButtons && !showAllMarkersList && search.trim() === '' && !showGroupDropdown && (
                <div className="search-mode-popup">
                  <button
                    type="button"
                    className={searchMode === 'markers' ? 'active' : ''}
                    aria-pressed={searchMode === 'markers'}
                    onClick={() => {
                      if (searchMode === 'markers') {
                        // show full marker list for selection
                        setSearchActive(true);
                        try { searchInputRef.current && searchInputRef.current.focus(); } catch (e) {}
                        const filteredMarkers = customFilterActive ? bajaMarkers.filter(m => customSelectedMarkers.includes(m.name)) : bajaMarkers.filter(m => activeGroups.includes(m.group));
                        // if no markers are available because filters hide them, show a warning instead
                        if (!filteredMarkers || filteredMarkers.length === 0) {
                          setNoMarkersWarningOpen(true);
                          // also close the small popup to avoid overlap
                          setShowSearchButtons(false);
                          return;
                        }
                        // sort alphabetically by name for the full list
                        setSearchResults([...filteredMarkers].sort((a, b) => (a.name || a.description || '').toString().localeCompare((b.name || b.description || '').toString())));
                        // Expand groups that are currently active so users see relevant markers immediately
                        setExpandedGroups(activeGroups.length > 0 ? activeGroups.slice() : []);
                        setShowAllMarkersList(true);
                        // hide the small mode popup while the full list is shown
                        setShowSearchButtons(false);
                      } else {
                        setSearchMode('markers');
                        setSearchActive(true);
                        setShowAllMarkersList(false);
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 4}}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    {searchMode === 'markers' ? 'Markers - Show list' : 'Markers'}
                  </button>
                  <button
                    type="button"
                    className={searchMode === 'locations' ? 'active' : ''}
                    aria-pressed={searchMode === 'locations'}
                    onClick={() => setSearchMode('locations')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 4}}><circle cx="12" cy="10" r="3"/><path d="M12 2v2m0 16v2m10-10h-2M4 12H2m15.07-7.07l-1.41 1.41M6.34 17.66l-1.41 1.41m12.02 0l1.41-1.41M6.34 6.34L4.93 4.93"/></svg>
                    Locations
                  </button>
                </div>
              )}

              {!isMobile && (
                <div style={{ marginLeft: 8 }}>
                  <CustomMapTypeControl mapInstance={mapInstance.current} inline={true} />
                </div>
              )}

              {/* Group the three icons so we can center them on desktop without touching mobile rules */}
              <div className="search-icons" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 6,
                    marginRight: 2,
                    color: showGroupDropdown ? '#1976d2' : '#888',
                    fontSize: 22,
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: 4,
                    padding: 2,
                    background: showGroupDropdown ? '#e3eaf6' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                  onClick={e => { e.stopPropagation(); setShowGroupDropdown(v => !v); }}
                  tabIndex={0}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 4 21 4 14 14 14 21 10 21 10 14 3 4" /></svg>
                </span>

                {/* (Custom filter controls moved into the group dropdown) */}

                <div style={{ position: 'relative' }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: 4,
                      marginRight: 8,
                      color: measuring ? '#388e3c' : '#888',
                      fontSize: 22,
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderRadius: 4,
                      padding: 2,
                      background: measuring ? '#e3f6e3' : 'transparent',
                      transition: 'background 0.2s',
                    }}
                    onClick={() => {
                      // Toggle measuring and options; hide other popups to avoid overlap
                      setMeasuring(prev => {
                        const next = !prev;
                        setShowMeasureOptions(next ? !showMeasureOptionsRef.current : false);
                        // hide search mode popup and group dropdown so only measurement UI shows
                        setShowSearchButtons(false);
                        setShowGroupDropdown(false);
                        return next;
                      });
                    }}
                    title="Measure (click to open options)"
                    tabIndex={0}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="6" rx="2"/><path d="M6 7v6M10 7v6M14 7v6M18 7v6"/></svg>
                  </span>
                  {/* Suboptions popup */}
                  {measuring && showMeasureOptions && (
                    <div className="search-mode-popup" style={{ position: 'absolute', top: '100%', zIndex: 10005, minWidth: 240, padding: 12 }}>
                      <div style={{ height: 12, marginBottom: 8 }} />
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <button onClick={() => { setMeasurementMode('distance'); }} className={measurementMode === 'distance' ? 'active' : ''} style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: measurementMode === 'distance' ? '1.5px solid #1976d2' : '1px solid #eee', background: measurementMode === 'distance' ? '#e8f5ff' : '#fff', cursor: 'pointer', fontWeight: 600, color: measurementMode === 'distance' ? '#000' : '#1976d2' }}>Distance</button>
                        <button onClick={() => { setMeasurementMode('radius'); }} className={measurementMode === 'radius' ? 'active' : ''} style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: measurementMode === 'radius' ? '1.5px solid #1976d2' : '1px solid #eee', background: measurementMode === 'radius' ? '#e8f5ff' : '#fff', cursor: 'pointer', fontWeight: 600, color: measurementMode === 'radius' ? '#000' : '#1976d2' }}>Anchor Swing Cycle</button>
                      </div>
                      {measurementMode === 'radius' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, marginRight: 8 }}>Radius:</span>
                            <input type="number" value={radiusMeters} onChange={e => setRadiusMeters(Number(e.target.value || 0))} style={{ width: 110, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontWeight: 600 }} />
                            <div style={{ fontSize: 13, color: '#666' }}>meters</div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {[50,100,250,500,1000].map(p => (
                              <button key={p} onClick={() => setRadiusMeters(p)} style={{ padding: '6px 10px', borderRadius: 16, border: radiusMeters === p ? '1.5px solid #1976d2' : '1px solid #eee', background: radiusMeters === p ? '#e3f6ff' : '#fff', cursor: 'pointer', fontSize: 13 }}>{p}m</button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                        {measurementMode !== 'radius' && (
                          <button onClick={() => { setMeasurePoints(prev => (prev && prev.length > 0) ? prev.slice(0, -1) : []); }} title="Undo last point" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                            Undo
                          </button>
                        )}
                        {measurementMode === 'radius' ? (
                          <>
                            <button onClick={() => { try { clearRadius(); } catch (e) {} }} title="Clear" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                              Clear
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => {
                              setMeasurePoints([]);
                              if (measureLine) { measureLine.setMap(null); setMeasureLine(null); }
                              if (measureStartMarker) { measureStartMarker.setMap(null); setMeasureStartMarker(null); }
                              if (measureEndMarker) { measureEndMarker.setMap(null); setMeasureEndMarker(null); }
                              if (window.measureInfoWindow) { window.measureInfoWindow.close(); window.measureInfoWindow = null; }
                              try { measureSegmentWindowsRef.current.forEach(w => { try { if (w && w.close) w.close(); else if (w && w.setMap) w.setMap(null); } catch (e) {} }); measureSegmentWindowsRef.current = []; } catch (e) {}
                            }} title="Clear measurement" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                              Clear
                            </button>
                            <button onClick={() => setShowSegmentLabels(v => !v)} title="Toggle segment labels" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: showSegmentLabels ? '#e3f6e3' : '#fff', cursor: 'pointer' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/></svg>
                              {showSegmentLabels ? 'Hide segments' : 'Show segments'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* measurement controls were moved to a floating panel below the search bar when measuring active */}
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 4,
                    marginRight: 8,
                    color: locationActive ? '#1976d2' : '#888',
                    fontSize: 22,
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: 4,
                    padding: 2,
                    background: locationActive ? '#e8f0fe' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                  onClick={handleLocateClick}
                  title={locationActive ? 'Disable current location' : (locating ? 'Locating...' : 'Show my current location')}
                  tabIndex={0}
                >
                  {/* GPS ripple: dot + outer ring */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="12" r="6" />
                  </svg>
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 4,
                    marginRight: 8,
                    color: clusteringEnabled ? '#1976d2' : '#888',
                    fontSize: 22,
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderRadius: 4,
                    padding: 2,
                    background: clusteringEnabled ? '#e8f0fe' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                  onClick={() => { setClusteringEnabled(v => !v); setShowSearchButtons(false); }}
                  title={clusteringEnabled ? 'Disable marker clustering' : 'Enable marker clustering'}
                  tabIndex={0}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="12" r="2" />
                    <circle cx="12" cy="8" r="2" />
                    <circle cx="16" cy="14" r="2" />
                    <path d="M6 17c1-2 3-3 6-3s5 1 6 3" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {showGroupDropdown && search.trim() === '' && (
            <div
              ref={filterDropdownRef}
              style={{
                position: 'absolute',
                top: 56,
                left: 0,
                background: '#fff',
                border: '1px solid #aaa',
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: 8,
                zIndex: 10002,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 180,
                maxWidth: 320,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (customFilterActive) return; // disabled while custom active
                  if (activeGroups.length === uniqueGroups.length) setActiveGroups([]); else setActiveGroups(uniqueGroups);
                }}
                style={{ background: customFilterActive ? '#f3f4f6' : '#1976d2', color: customFilterActive ? '#999' : '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 15, fontWeight: 500, marginBottom: 6, cursor: customFilterActive ? 'not-allowed' : 'pointer', alignSelf: 'flex-end', pointerEvents: customFilterActive ? 'none' : 'auto' }}
                disabled={customFilterActive}
              >
                {activeGroups.length === uniqueGroups.length ? 'Hide All' : 'Show All'}
              </button>
              {uniqueGroups.map(group => {
                const icon = getGroupIcon(group);
                const isActive = activeGroups.includes(group);
                const disabled = customFilterActive;
                const groupBtnStyle: React.CSSProperties = { background: isActive ? '#e3eaf6' : 'none', color: isActive ? '#1976d2' : (disabled ? '#999' : '#222'), border: 'none', padding: '6px 0', cursor: disabled ? 'not-allowed' : 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left', borderRadius: 6, transition: 'background 0.2s, color 0.2s', opacity: disabled ? 0.7 : 1, pointerEvents: disabled ? 'none' : 'auto' };
                return (
                  <button key={group} onClick={e => { e.stopPropagation(); if (!disabled) toggleGroup(group); }} style={groupBtnStyle} disabled={disabled}>
                    <span style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <img src={icon} alt="icon" style={{ width: 22, height: 22, marginRight: 10, verticalAlign: 'middle', boxShadow: isActive ? '0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,0.10)' : undefined, borderRadius: '50%', background: isActive ? '#fff' : 'transparent' }} />
                      <span style={{ fontSize: 15, fontWeight: isActive ? 600 : 400 }}>{group}</span>
                    </span>
                    <span style={{ color: isActive ? '#bbdefb' : '#888', fontSize: 13, minWidth: 24, textAlign: 'right' }}>{bajaMarkers.filter(m => m.group === group).length}</span>
                  </button>
                );
              })}
              {/* Custom filter entry */}
              <div style={{ height: 1, background: '#f6f7f9', margin: '6px 0' }} />
              <div key="__custom_filter__" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault();
                    // toggle custom filter and manage saved group selection
                    if (!customFilterActive) {
                      previousGroupsRef.current = activeGroups.slice();
                      setActiveGroups([]); // switch to hide-all mode
                      setCustomFilterActive(true);
                    } else {
                      setCustomFilterActive(false);
                      if (previousGroupsRef.current) { setActiveGroups(previousGroupsRef.current); previousGroupsRef.current = null; }
                    }
                  } }}
                  onClick={e => { e.stopPropagation();
                    if (!customFilterActive) {
                      previousGroupsRef.current = activeGroups.slice();
                      setActiveGroups([]);
                      setCustomFilterActive(true);
                    } else {
                      setCustomFilterActive(false);
                      if (previousGroupsRef.current) { setActiveGroups(previousGroupsRef.current); previousGroupsRef.current = null; }
                    }
                  }}
                  style={{ background: customFilterActive ? '#e3eaf6' : 'none', color: customFilterActive ? '#1976d2' : '#222', padding: '6px 0', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left', borderRadius: 6 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={'images/icon-customfilter.png'} alt="icon" style={{ width: 22, height: 22, marginRight: 8, verticalAlign: 'middle', borderRadius: '50%' }} />
                    <span style={{ fontSize: 15, fontWeight: customFilterActive ? 600 : 400 }}>Custom</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Export JSON button */}
                    <button onClick={e => { e.stopPropagation(); handleExportCustom(); }} title="Export custom selection" style={{ border: 'none', background: 'transparent', padding: 6, cursor: 'pointer', color: '#666' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </button>
                    {/* Import JSON button */}
                    <button onClick={e => { e.stopPropagation(); handleImportClick(); }} title="Import custom selection" style={{ border: 'none', background: 'transparent', padding: 6, cursor: 'pointer', color: '#666' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 5 17 10"/><line x1="12" y1="5" x2="12" y2="21"/></svg>
                    </button>
                    {/* Edit button */}
                    <button onClick={e => { e.stopPropagation(); setShowCustomEditor(true); setCustomEditorQuery(''); }} title="Edit custom filter" style={{ border: 'none', background: 'transparent', padding: 6, cursor: 'pointer', color: '#666' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                    </button>
                    <span style={{ color: customFilterActive ? '#bbdefb' : '#888', fontSize: 13 }}>{customSelectedMarkers.length}</span>
                    {/* Hidden file input for importing JSON */}
                    <input ref={el => importInputRef.current = el} type="file" accept="application/json" onChange={handleImportFile} style={{ display: 'none' }} />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Portal-based Custom filter editor modal (renders into document.body to avoid transform clipping) */}
          {showCustomEditor && createPortal(
            <div style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 20050 }} onClick={() => { setShowCustomEditor(false); setShowGroupDropdown(true); }}>
              <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'min(860px, 95vw)', maxHeight: '80vh', background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 12px 48px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>Edit Custom Filter</h3>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setShowCustomEditor(false); setShowGroupDropdown(true); }} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                  <input placeholder="Search markers..." value={customEditorQuery} onChange={e => setCustomEditorQuery(e.target.value)} style={{ padding: 8, fontSize: 16, borderRadius: 8, border: '1px solid #ccc', flex: 1 }} />
                  <button onClick={() => { setCustomSelectedMarkers(bajaMarkers.map(m => m.name)); }} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Select all</button>
                  <button onClick={() => { setCustomSelectedMarkers([]); }} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Deselect all</button>
                </div>
                <div style={{ overflowY: 'auto', paddingRight: 8 }}>
                  {/* Single checkbox entry to control all 'National Park Information' markers */}
                  {(() => {
                    const groupName = 'National Park Information';
                    const parkMarkers = bajaMarkers.filter(m => m.group === groupName).map(m => m.name);
                    const allParksSelected = parkMarkers.every(n => customSelectedMarkers.includes(n)) && parkMarkers.length > 0;
                    const anyParkSelected = parkMarkers.some(n => customSelectedMarkers.includes(n));
                    if (parkMarkers.length > 0 && (!customEditorQuery || groupName.toLowerCase().includes(customEditorQuery.toLowerCase()))) {
                      return (
                        <div key="__np_group__" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input type="checkbox" checked={allParksSelected} onChange={() => {
                              setCustomSelectedMarkers(prev => {
                                const prevSet = new Set(prev);
                                if (allParksSelected) {
                                  // deselect all park markers
                                  parkMarkers.forEach(n => prevSet.delete(n));
                                } else {
                                  // select all park markers
                                  parkMarkers.forEach(n => prevSet.add(n));
                                }
                                return Array.from(prevSet);
                              });
                            }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div style={{ fontSize: 15, fontWeight: anyParkSelected ? 600 : 500 }}>{groupName}</div>
                              <div style={{ fontSize: 13, color: '#666' }}>{parkMarkers.length} markers</div>
                            </div>
                          </label>
                          <div style={{ fontSize: 13 }}>{groupName}</div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {bajaMarkers.filter(m => m.group !== 'National Park Information' && (!customEditorQuery || m.name.toLowerCase().includes(customEditorQuery.toLowerCase()))).map((m) => {
                    const checked = customSelectedMarkers.includes(m.name);
                    return (
                      <div key={m.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 6px', borderBottom: '1px solid #f0f0f0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input type="checkbox" checked={checked} onChange={() => {
                            setCustomSelectedMarkers(prev => prev.includes(m.name) ? prev.filter(x => x !== m.name) : [...prev, m.name]);
                          }} />
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontSize: 15, fontWeight: 500 }}>{m.name}</div>
                            <div style={{ fontSize: 13, color: '#666' }}>{m.group}</div>
                          </div>
                        </label>
                        <div style={{ fontSize: 13 }}>{m.group}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button onClick={() => { setCustomSelectedMarkers([]); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Clear</button>
                  <button onClick={() => { setShowCustomEditor(false); setShowGroupDropdown(true); }} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            </div>, document.body
          )}

            {noMarkersWarningOpen && createPortal(
              <div style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 20050 }} onClick={() => { setNoMarkersWarningOpen(false); }}>
                <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'min(520px, 94vw)', background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
                  <h3 style={{ marginTop: 0 }}>No markers available</h3>
                  <div style={{ marginBottom: 12, color: '#444' }}>It looks like all markers are currently filtered out. Enable some filter categories.</div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setNoMarkersWarningOpen(false); setShowGroupDropdown(true); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Open filters</button>
                    <button onClick={() => { setNoMarkersWarningOpen(false); }} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>OK</button>
                  </div>
                </div>
              </div>, document.body
            )}

          {(searchActive && ((search.trim() !== '' && searchResults.length > 0) || (showAllMarkersList && searchResults.length > 0))) && (
            <div ref={fullListRef} style={{ background: '#fff', border: '1px solid #aaa', borderTop: 'none', maxHeight: 320, overflow: 'hidden', borderRadius: '0 0 8px 8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'block', position: 'absolute', top: 56, left: 0, width: '100%', pointerEvents: 'auto', zIndex: 10003 }}>
              {/* Header area: absolute X on top-left and title centered */}
              {showAllMarkersList && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 8, background: '#fafafa', borderBottom: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => { setShowAllMarkersList(false); setShowSearchButtons(true); try { searchInputRef.current && searchInputRef.current.focus(); } catch (e) {} }} aria-label="Close list" style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>&times;</button>
                    <div style={{ fontWeight: 600 }}>{searchResults.length} markers</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* Single toggle: Show all / Hide all */}
                    <button
                      title="Toggle show/hide all groups"
                      onClick={() => {
                        try {
                          const list = showAllMarkersList ? [...searchResults] : searchResults;
                          const groups: { [k: string]: any[] } = {};
                          list.forEach((m: any) => { const g = (m.group && m.group.toString()) || 'Other'; if (!groups[g]) groups[g] = []; groups[g].push(m); });
                          const keys = Object.keys(groups);
                          const allExpanded = keys.length > 0 && keys.every(k => expandedGroups.includes(k));
                          if (allExpanded) setExpandedGroups([]);
                          else setExpandedGroups(keys);
                        } catch (e) {
                          setExpandedGroups([]);
                        }
                      }}
                      style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {(() => {
                        try {
                          const list = showAllMarkersList ? [...searchResults] : searchResults;
                          const groups: { [k: string]: any[] } = {};
                          list.forEach((m: any) => { const g = (m.group && m.group.toString()) || 'Other'; if (!groups[g]) groups[g] = []; groups[g].push(m); });
                          const keys = Object.keys(groups);
                          const allExpanded = keys.length > 0 && keys.every(k => expandedGroups.includes(k));
                          return allExpanded ? 'Hide all' : 'Show all';
                        } catch (e) { return 'Show all'; }
                      })()}
                    </button>
                  </div>
                </div>
              )}
              {/* Scrollable results area so header remains visible */}
              <div style={{ maxHeight: 320 - 48, overflowY: 'auto' }}>
                {(() => {
                  // Build a list from searchResults. For location searches we show a flat dropdown
                  // (no grouping/fallback to 'Other') so place predictions appear directly.
                  const list = showAllMarkersList ? [...searchResults].sort((a, b) => (a.name || a.description || '').toString().localeCompare((b.name || b.description || '').toString())) : searchResults;

                  if (searchMode === 'locations') {
                    // Render flat list for place predictions (each item has description and place_id)
                    return list.map((m: any, idx: number) => {
                      const key = (m.place_id || m.description || idx) + String(idx);
                      const isLast = idx === list.length - 1;
                      return (
                        <div
                          key={key}
                          style={{ padding: '10px 20px', cursor: 'pointer', borderBottom: isLast ? 'none' : '1px solid #eee', fontSize: 17, background: '#fff' }}
                          onClick={async () => {
                            if (!mapInstance.current) return;
                            try { if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; } } catch (e) {}
                            if (locationMarkerRef.current) { locationMarkerRef.current.setMap(null); locationMarkerRef.current = null; }
                            // If we have a Places service and a place_id, prefer getDetails for precise geometry
                            if (placesServiceRef.current && m.place_id) {
                              placesServiceRef.current.getDetails({ placeId: m.place_id }, (place: any, status: any) => {
                                if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                                  const loc = place.geometry.location;
                                  mapInstance.current.panTo({ lat: loc.lat(), lng: loc.lng() });
                                  mapInstance.current.setZoom(13);
                                  const marker = new (window as any).google.maps.Marker({ map: mapInstance.current, position: loc, title: place.formatted_address || place.name });
                                  locationMarkerRef.current = marker;
                                }
                              });
                            } else if (m.place_id) {
                              // fallback to geocode by place_id
                              fetch(`https://maps.googleapis.com/maps/api/geocode/json?place_id=${m.place_id}&key=${GOOGLE_MAPS_API_KEY}`).then(r => r.json()).then(data => {
                                const res = data.results && data.results[0];
                                if (res && res.geometry && res.geometry.location) {
                                  const loc = res.geometry.location;
                                  mapInstance.current.panTo({ lat: loc.lat, lng: loc.lng });
                                  mapInstance.current.setZoom(13);
                                  const marker = new window.google.maps.Marker({ map: mapInstance.current, position: loc, title: res.formatted_address || m.description });
                                  locationMarkerRef.current = marker;
                                }
                              }).catch(() => {});
                            } else if (m.description) {
                              // No place_id — try a simple geocode by address text
                              fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(m.description)}&key=${GOOGLE_MAPS_API_KEY}`).then(r => r.json()).then(data => {
                                const res = data.results && data.results[0];
                                if (res && res.geometry && res.geometry.location) {
                                  const loc = res.geometry.location;
                                  mapInstance.current.panTo({ lat: loc.lat, lng: loc.lng });
                                  mapInstance.current.setZoom(13);
                                  const marker = new window.google.maps.Marker({ map: mapInstance.current, position: loc, title: res.formatted_address || m.description });
                                  locationMarkerRef.current = marker;
                                }
                              }).catch(() => {});
                            }
                            setSearchActive(false); setSearch(''); setShowGroupDropdown(false); setShowAllMarkersList(false);
                          }}
                          onMouseDown={e => e.preventDefault()}
                        >
                          {m.description || m.name}
                        </div>
                      );
                    });
                  }

                  // Default behavior for marker search results: group by category as before
                  // Group markers by category
                  const groups: { [k: string]: any[] } = {};
                  list.forEach((m) => {
                    const g = (m.group && m.group.toString()) || 'Other';
                    if (!groups[g]) groups[g] = [];
                    groups[g].push(m);
                  });
                  // Sort group names: put 'Other' last
                  const groupNames = Object.keys(groups).sort((a, b) => {
                    if (a === 'Other') return 1;
                    if (b === 'Other') return -1;
                    return a.localeCompare(b);
                  });
                  return groupNames.map((groupName) => {
                    const items = groups[groupName] || [];
                    const isExpanded = expandedGroups.includes(groupName);
                    const icon = getGroupIcon(groupName);
                    return (
                      <div key={groupName} style={{ borderBottom: '1px solid #f2f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', cursor: 'pointer' }} onClick={() => {
                          setExpandedGroups(prev => prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{isExpanded ? '▾' : '▸'}</div>
                            <img src={icon} alt="" style={{ width: 20, height: 20, marginRight: 6, borderRadius: '50%', background: isExpanded ? '#fff' : 'transparent' }} />
                            <div style={{ fontWeight: 700 }}>{groupName}</div>
                            <div style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>{items.length}</div>
                          </div>
                          <div style={{ width: 12 }} />
                        </div>
                        {isExpanded && (
                          <div>
                            {items.map((m: any, idx: number) => {
                              const key = (m.name || m.description || m.place_id || idx) + idx;
                              const isLast = idx === items.length - 1;
                              return (
                                <div key={key} style={{ padding: '10px 20px', cursor: 'pointer', borderBottom: isLast ? 'none' : '1px solid #eee', fontSize: 17, background: '#fff' }} onClick={async () => {
                                  if (!mapInstance.current) return;
                                  try { if (locationMarkerListenerRef.current) { locationMarkerListenerRef.current.remove(); locationMarkerListenerRef.current = null; } } catch (e) {}
                                  if (locationMarkerRef.current) { locationMarkerRef.current.setMap(null); locationMarkerRef.current = null; }
                                  if (searchMode === 'markers') {
                                    mapInstance.current.panTo({ lat: m.lat, lng: m.lng }); mapInstance.current.setZoom(15); openPopup({ title: m.name, images: m.images && Array.isArray(m.images) ? m.images : [], description: m.description }); setImgIdx(0); setLightboxOpen(false);
                                  } else {
                                    if (placesServiceRef.current && m.place_id) {
                                      placesServiceRef.current.getDetails({ placeId: m.place_id }, (place: any, status: any) => { if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place.geometry) { const loc = place.geometry.location; mapInstance.current.panTo({ lat: loc.lat(), lng: loc.lng() }); mapInstance.current.setZoom(13); const marker = new (window as any).google.maps.Marker({ map: mapInstance.current, position: loc, title: place.formatted_address || place.name }); locationMarkerRef.current = marker; } });
                                    } else {
                                      fetch(`https://maps.googleapis.com/maps/api/geocode/json?place_id=${m.place_id}&key=${GOOGLE_MAPS_API_KEY}`).then(r => r.json()).then(data => { const res = data.results && data.results[0]; if (res && res.geometry && res.geometry.location) { const loc = res.geometry.location; mapInstance.current.panTo({ lat: loc.lat, lng: loc.lng }); mapInstance.current.setZoom(13); const marker = new window.google.maps.Marker({ map: mapInstance.current, position: loc, title: res.formatted_address || m.description }); locationMarkerRef.current = marker; } }).catch(() => {});
                                    }
                                  }
                                  setSearchActive(false); setSearch(''); setShowGroupDropdown(false); setShowAllMarkersList(false);
                                }} onMouseDown={e => e.preventDefault()}>{m.name || m.description}</div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating measurement panel removed; actions are now inside the single measurement popup */}

      <div ref={mapRef} style={containerStyle as React.CSSProperties} />

      {popup && (popup.title || (popup.description && popup.description.replace(/<[^>]+>/g, '').trim()) || (popup.images && popup.images.length > 0)) && (
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            pointerEvents: 'auto', // allow pointer events on overlay
            // Allow touch-based vertical scrolling inside the popup on mobile
            touchAction: 'auto',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            ...(uiHiddenStyle || {}),
            // mark popup root so it can be hidden even if outside normal tree
          }}
          data-hide-in-streetview
          onClick={e => {
            if (e.target === popupRef.current) {
              setPopup(null);
              setLightboxOpen(false);
            }
          }}
          onContextMenu={e => e.preventDefault()}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              // allow popup to span full viewport width on small screens, but cap on larger screens
              maxWidth: '550px',
              width: '100vw',
              textAlign: 'center',
              boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
              position: 'relative',
              pointerEvents: 'auto', // Allow interaction with popup content
              // keep rounded corners visible by hiding overflow on the outer container
              overflow: 'hidden',
              boxSizing: 'border-box',
              WebkitUserSelect: 'text',
              userSelect: 'text',
            }}
            onClick={e => e.stopPropagation()}
            onContextMenu={e => e.preventDefault()}
          >
            <div style={{
              // Scrollable inner area: subtract outer vertical padding (24 + 24 = 48px)
              maxHeight: 'calc(80vh - 48px)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              boxSizing: 'border-box',
              width: '100%'
            }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <h2 style={{ margin: 0 }}>{popup.title}</h2>
              {/* Download PDF icon button */}
              <button
                title="Download Marker Info as PDF"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 8 }}
                onClick={() => handleDownloadMarker({
                  title: popup.title,
                  lat: bajaMarkers.find(m => m.name === popup.title)?.lat ?? 0,
                  lng: bajaMarkers.find(m => m.name === popup.title)?.lng ?? 0,
                  description: popup.description || ''
                })}
              >
                {/* Simple download SVG icon */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
              {/* Favorite (custom) toggle button */}
              {bajaMarkers.find(m => m.name === popup.title) && (
                <button
                  title={customSelectedMarkers.includes(popup.title) ? 'Remove from custom filter' : 'Will be added to custom filter'}
                  onClick={() => toggleFavorite(popup.title)}
                  style={{ background: customSelectedMarkers.includes(popup.title) ? '#1976d2' : 'none', border: 'none', cursor: 'pointer', padding: 6, marginLeft: 8, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <img src={'images/icon-customfilter.png'} alt="custom" style={{ width: 20, height: 20, display: 'block', filter: customSelectedMarkers.includes(popup.title) ? 'brightness(0) invert(1)' : 'none' }} />
                </button>
              )}
            </div>
            {/* If this popup corresponds to a marker, add a visible divider between title and content */}
            {bajaMarkers.find(m => m.name === popup.title) && (
              <div style={{ width: '100%', height: 1, background: '#028090', margin: '10px 0' }} />
            )}

          {/* (Custom editor modal moved to top-level so it can open anytime) */}

            {/* Only show image/lightbox UI if there are non-PDF images */}
            {popupNonPdfImages && popupNonPdfImages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', marginBottom: 16, position: 'relative', width: '100%', maxWidth: 400, height: 240, justifyContent: 'center', margin: '0 auto', alignSelf: 'center' }}>
                <div
                  style={{ position: 'relative', width: '100%', maxWidth: 400, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseDown={e => e.preventDefault()}
                  onTouchStart={isTouchDevice ? (e => {
                    if (e.touches && e.touches.length === 1) {
                      touchStartX.current = e.touches[0].clientX;
                    }
                  }) : undefined}
                  onTouchMove={isTouchDevice ? (e => {
                    if (e.touches && e.touches.length === 1) {
                      touchEndX.current = e.touches[0].clientX;
                    }
                  }) : undefined}
                  onTouchEnd={isTouchDevice ? (() => {
                    if (touchStartX.current !== null && touchEndX.current !== null) {
                      const dx = touchEndX.current - touchStartX.current;
                      if (Math.abs(dx) > 40) {
                        if (dx < 0) {
                          // Swipe left: next image
                          setImgIdx(prev => (prev === popupNonPdfImages.length - 1 ? 0 : prev + 1));
                        } else {
                          // Swipe right: previous image
                          setImgIdx(prev => (prev === 0 ? popupNonPdfImages.length - 1 : prev - 1));
                        }
                      }
                    }
                    touchStartX.current = null;
                    touchEndX.current = null;
                  }) : undefined}
                >
                  {/* Left hover area for previous */}
                  {popupNonPdfImages.length > 1 && (
                    <div
                      onClick={e => {
                        e.stopPropagation();
                        setImgIdx(prev => (prev === 0 ? popupNonPdfImages.length - 1 : prev - 1));
                      }}
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '30%',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        pointerEvents: 'auto',
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                        userSelect: 'none',
                      }}
                      tabIndex={-1}
                      onMouseEnter={!isTouchDevice ? (e => {
                        const span = e.currentTarget.querySelector('span');
                        if (span) {
                          span.style.transform = 'scale(1.25)';
                        }
                      }) : undefined}
                      onMouseLeave={!isTouchDevice ? (e => {
                        const span = e.currentTarget.querySelector('span');
                        if (span) {
                          span.style.transform = 'scale(1)';
                        }
                      }) : undefined}
                    >
                      <span style={{ marginLeft: 8, fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 40, height: 80, lineHeight: '80px', display: 'inline-block', textShadow: 'none', color: '#bbb', opacity: 0.7, transition: 'transform 0.18s', width: 40, textAlign: 'center', outline: 'none', userSelect: 'none' }}>&lt;</span>
                    </div>
                  )}
                  {/* Right hover area for next */}
                  {popupNonPdfImages.length > 1 && (
                    <div
                      onClick={e => {
                        e.stopPropagation();
                        setImgIdx(prev => (prev === popupNonPdfImages.length - 1 ? 0 : prev + 1));
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '30%',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pointerEvents: 'auto',
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                        userSelect: 'none',
                      }}
                      tabIndex={-1}
                      onMouseEnter={!isTouchDevice ? (e => {
                        const span = e.currentTarget.querySelector('span');
                        if (span) {
                          span.style.transform = 'scale(1.25)';
                        }
                      }) : undefined}
                      onMouseLeave={!isTouchDevice ? (e => {
                        const span = e.currentTarget.querySelector('span');
                        if (span) {
                          span.style.transform = 'scale(1)';
                        }
                      }) : undefined}
                    >
                      <span style={{ marginRight: 8, fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 40, height: 80, lineHeight: '80px', display: 'inline-block', textShadow: 'none', color: '#bbb', opacity: 0.7, transition: 'transform 0.18s', width: 40, textAlign: 'center', outline: 'none', userSelect: 'none' }}>&gt;</span>
                    </div>
                  )}
                  {/* Main image */}
                  <img
                    className="popup-img"
                    src={popupNonPdfImages[imgIdx]}
                    alt={popup.title + ' ' + (imgIdx + 1)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, cursor: 'zoom-in', userSelect: 'none', zIndex: 1, position: 'relative' }}
                    onClick={e => {
                      e.stopPropagation();
                      if (justOpenedRef.current) return;
                      setLightboxOpen(true);
                    }}
                    title="Click to enlarge"
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onMouseDown={e => e.preventDefault()}
                  />
                  {/* Image index indicator */}
                  {/* Removed image index indicator */}
                </div>
              </div>
            )}
            {/* Lightbox overlay for full-size image */}
            {popupNonPdfImages && popupNonPdfImages.length > 0 && lightboxOpen && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(0,0,0,0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  touchAction: 'none',
                  overflow: 'hidden',
                }}
                onClick={() => setLightboxOpen(false)}
              >
                <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                  <img
                    className="popup-lightbox-img"
                    src={popupNonPdfImages[imgIdx]}
                    alt={popup.title + ' ' + (imgIdx + 1)}
                    style={{
                      maxWidth: !isTouchDevice && desktopZoomed ? 'none' : '90vw',
                      maxHeight: !isTouchDevice && desktopZoomed ? 'none' : '80vh',
                      borderRadius: 8,
                      boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
                      display: 'block',
                      userSelect: 'none',
                      cursor: isTouchDevice
                        ? (zoom > 1 ? 'grab' : 'zoom-in')
                        : (desktopZoomed ? (desktopDragging ? 'grabbing' : 'grab') : 'zoom-in'),
                      transform: isTouchDevice
                        ? `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`
                        : (desktopZoomed ? `scale(2) translate(${desktopPan.x}px, ${desktopPan.y}px)` : undefined),
                      transformOrigin: 'center center',
                      transition: dragStart ? 'none' : 'transform 0.2s',
                    }}
                    draggable={false}
                    onDragStart={e => e.preventDefault()}
                    onMouseDown={handleDesktopMouseDown}
                    onMouseMove={handleDesktopMouseMove}
                    onMouseUp={handleDesktopMouseUp}
                    onMouseLeave={() => setDesktopDragging(false)}
                    {...(isTouchDevice ? {
                      onPointerDown: (e: React.PointerEvent<HTMLImageElement>) => {
                        (e.target as HTMLElement).setPointerCapture(e.pointerId);
                        pointerMap.current.set(e.pointerId, e.nativeEvent as PointerEvent);
                        if (pointerMap.current.size === 2) {
                          // Pinch start
                          const points = Array.from(pointerMap.current.values()) as PointerEvent[];
                          const dx = points[1].clientX - points[0].clientX;
                          const dy = points[1].clientY - points[0].clientY;
                          setLastTouchDist(Math.sqrt(dx * dx + dy * dy));
                          setLastTouchCenter({ x: (points[0].clientX + points[1].clientX) / 2, y: (points[0].clientY + points[1].clientY) / 2 });
                        } else if (pointerMap.current.size === 1) {
                          const pt = e.nativeEvent as PointerEvent;
                          setDragStart({ x: pt.clientX - offset.x, y: pt.clientY - offset.y });
                          // Double tap to reset zoom
                          const now = Date.now();
                          if ((window as any)._lastTap && now - (window as any)._lastTap < 350) {
                            setZoom(1);
                            setOffset({ x: 0, y: 0 });
                            setDragStart(null);
                            setLastTouchDist(null);
                            setLastTouchCenter(null);
                          }
                          (window as any)._lastTap = now;
                        }
                      },
                      onPointerMove: (e: React.PointerEvent<HTMLImageElement>) => {
                        if (!pointerMap.current.has(e.pointerId)) return;
                        pointerMap.current.set(e.pointerId, e.nativeEvent as PointerEvent);
                        if (pointerMap.current.size === 2) {
                          // Pinch zoom
                          const points = Array.from(pointerMap.current.values()) as PointerEvent[];
                          const dx = points[1].clientX - points[0].clientX;
                          const dy = points[1].clientY - points[0].clientY;
                          const dist = Math.sqrt(dx * dx + dy * dy);
                          if (lastTouchDist && lastTouchCenter) {
                            setZoom(z => Math.max(1, Math.min(4, z * (dist / lastTouchDist))));
                          }
                          setLastTouchDist(dist);
                          setLastTouchCenter({ x: (points[0].clientX + points[1].clientX) / 2, y: (points[0].clientY + points[1].clientY) / 2 });
                        } else if (pointerMap.current.size === 1 && dragStart) {
                          // Pan
                          const pt = e.nativeEvent as PointerEvent;
                          setOffset({ x: pt.clientX - dragStart.x, y: pt.clientY - dragStart.y });
                        }
                      },
                      onPointerUp: (e: React.PointerEvent<HTMLImageElement>) => {
                        pointerMap.current.delete(e.pointerId);
                        if (pointerMap.current.size < 2) {
                          setLastTouchDist(null);
                          setLastTouchCenter(null);
                        }
                        if (pointerMap.current.size === 0) {
                          setDragStart(null);
                        }
                      },
                      onPointerLeave: (e: React.PointerEvent<HTMLImageElement>) => {
                        pointerMap.current.delete(e.pointerId);
                        if (pointerMap.current.size < 2) {
                          setLastTouchDist(null);
                          setLastTouchCenter(null);
                        }
                        if (pointerMap.current.size === 0) {
                          setDragStart(null);
                        }
                      },
                    } : {})}
                  />
                  {(!isTouchDevice || zoom === 1) && (
                    <button
                      onClick={() => setLightboxOpen(false)}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 36,
                        height: 36,
                        fontSize: 24,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label="Close full image"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            )}
            <div
              style={{ marginBottom: 8, textAlign: 'left', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: popup.description }}
            />
            {/* Show embedded PDF preview using Google Docs Viewer, with Open/Download fallback */}
            {popupPdfDocs && popupPdfDocs.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>Additional information</div>
                {popupPdfDocs.map((u: string, idx: number) => (
                  <div key={u + idx} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <PdfEmbed url={u} />
                  </div>
                ))}
              </div>
            )}
            <button
              style={{
                marginTop: 16,
                padding: '8px 24px',
                fontSize: 16,
                border: 'none',
                background: '#1976d2',
                color: '#fff',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              onClick={() => {
                setPopup(null);
                setLightboxOpen(false);
              }}
            >
              Close
            </button>
            {/* Show latitude/longitude at bottom when popup matches a known marker */}
            {(() => {
              const m = bajaMarkers.find(mm => mm.name === popup.title);
              if (!m) return null;
              return (
                <div style={{ marginTop: 10, color: '#666', fontSize: 13, textAlign: 'center' }}>
                  Latitude: {m.lat?.toFixed(6) ?? 'n/a'} &nbsp;•&nbsp; Longitude: {m.lng?.toFixed(6) ?? 'n/a'}
                </div>
              );
            })()}
            </div>
          </div>
        </div>
      )}
      {/* Mobile logo at top left */}
      {isTouchDevice && (
        <img
          src={logoSeaOfCortezMobile}
          alt="Sea of Cortez Logo"
          style={{
            position: 'fixed',
            top: 4,
            left: 4,
            width: 40,
            height: 40,
            zIndex: 10010,
            background: 'rgba(255,255,255,0.85)',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            padding: 4,
            objectFit: 'contain',
            ...(uiHiddenStyle || {}),
          }}
        />
      )}
      {/* Custom map type control (Karte/Satellit) for mobile only (floating) */}
      {isMobile && (
        <div style={uiHiddenStyle}><CustomMapTypeControl mapInstance={mapInstance.current} /></div>
      )}
  {/* Legal modal */}
      {legalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.6)',
            zIndex: 30000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(uiHiddenStyle || {}),
          }}
          onClick={() => setLegalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: 32,
              borderRadius: 10,
              maxWidth: 600,
              width: '90vw',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                fontSize: 20,
                cursor: 'pointer',
              }}
              onClick={() => setLegalOpen(false)}
              aria-label="Close legal info"
            >
              &times;
            </button>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 15, color: '#222', background: 'none', margin: 0 }}>{legalText}</pre>
          </div>
        </div>
      )}
      {/* Legal notice link: fixed bottom center when logged in */}
      {passwordValid && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            width: '400px',
            maxWidth: '70vw',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            zIndex: 100,
            bottom: '25px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            boxSizing: 'border-box',
            background: 'none',
            ...(uiHiddenStyle || {}),
          }}
        >
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 15,
              padding: 0,
            }}
            onClick={() => setLegalOpen(true)}
          >
            Legal Notice / Privacy Policy / Terms
          </button>
        </div>
      )}
    </>
  );
}

// Simple inline PDF embed (direct PDF in iframe by default). Google Docs viewer can be toggled if desired.
function PdfEmbed({ url }: { url: string }) {
  // Force Google Docs viewer
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const src = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
  const stop = (e: any) => { try { e.stopPropagation(); } catch (err) {} };
  const [iframeKey, setIframeKey] = useState(() => `pdf-${Date.now()}`);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isStuck, setIsStuck] = useState(false);
  // Disable automatic retries: after the per-attempt watchdog (5s) the UI will show the manual retry button
  const maxAutoRetries = 0;

  // Recreate iframe whenever the URL changes to avoid stale/white frames
  useEffect(() => {
    setPdfLoaded(false);
    setIframeKey(`pdf-${Date.now()}`);
    setLoadAttempts(0);
    setIsStuck(false);
  }, [url]);

  // Watchdog: if iframe doesn't load within timeout, try to reload automatically a couple times,
  // then show a manual retry UI.
  useEffect(() => {
    // If already loaded, nothing to do
    if (pdfLoaded) {
      setIsStuck(false);
      return undefined;
    }

    // start a timeout to detect stuck loads
    const timeout = setTimeout(() => {
      if (!pdfLoaded) {
        if (loadAttempts < maxAutoRetries) {
          // auto retry: bump attempts and recreate iframe
          setLoadAttempts(a => a + 1);
          setIframeKey(`pdf-retry-${Date.now()}-${Math.random()}`);
        } else {
          // give up and show manual retry
          setIsStuck(true);
        }
      }
  }, 5000); // 5s watchdog (changed from 12s)

    return () => clearTimeout(timeout);
  }, [iframeKey, pdfLoaded, loadAttempts]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          width: isMobile ? '90%' : '100%',
          height: isMobile ? 360 : 420,
          margin: isMobile ? '0 auto' : undefined,
          background: '#f6f7f9',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #eee',
          position: 'relative'
        }}
        onPointerDown={stop}
        onPointerUp={stop}
        onPointerCancel={stop}
        onTouchStart={stop}
        onTouchEnd={stop}
      >
        {/*
          Overlay mask to visually hide and block the Google Docs viewer "enlarge/fullscreen" control.
          Because the PDF is embedded via an external iframe (cross-origin), we cannot access or alter
          the viewer's internal DOM. A small absolutely-positioned element placed over the expected
          icon location will both hide it visually and intercept clicks.

          Tradeoffs:
          - This is a visual workaround and may need adjustment if Google changes the toolbar layout.
          - It intentionally blocks clicks in the covered area so users cannot click that control.
          - If you want a more robust solution, consider using a direct PDF embed or a PDF.js viewer
            (gives full control over toolbar/buttons).
        */}
        {/* Top-right custom button that opens the PDF in a new tab.
            Replaces the previous opaque mask so users get a clear control.
            We stop propagation to avoid the iframe receiving pointer events from the click. */}
        <button
          onClick={(e) => {
            try { e.stopPropagation(); } catch (err) {}
            window.open(url, '_blank');
          }}
          title="Open PDF in new tab"
          aria-label="Open PDF in new tab"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 9999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 52,
            height: 52,
            padding: 0,
            background: '#ffffff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 10,
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
          }}
        >
          {/* Enlarge / maximize SVG icon */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M3 21h6v-2H5v-4H3v6zm0-10h2V7h4V5H3v6zm18 10v-6h-2v4h-4v2h6zM13 5h6v6h-2V7h-4V5z" fill="#1976d2" />
          </svg>
        </button>
        {/* Download button below the enlarge icon */}
        <button
          onClick={(e) => {
            try { e.stopPropagation(); } catch (err) {}
            // Programmatically create an anchor to trigger download; some servers may not set Content-Disposition
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', url.split('/').pop() || 'document.pdf');
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
          title="Download PDF"
          aria-label="Download PDF"
          style={{
            position: 'absolute',
            top: 68,
            right: 8,
            zIndex: 9999,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 52,
            height: 52,
            padding: 0,
            background: '#ffffff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 10,
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            {/* download icon: arrow down into a tray */}
            <path d="M5 20h14v-2H5v2zm7-12l4 4h-3v6h-2v-6H8l4-4z" fill="#1976d2" />
          </svg>
        </button>
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={src}
          title={`pdf-preview-${url}`}
          onLoad={() => setPdfLoaded(true)}
          onError={() => setPdfLoaded(false)}
          style={{ width: '100%', height: '100%', border: 0, touchAction: 'auto', position: 'relative', zIndex: 1 }}
        />

        {/* Loading indicator: shown while iframe hasn't fired load */}
        {!pdfLoaded && (
          // Allow clicks only when stuck (so Retry is clickable). While simply loading we keep pointerEvents none so iframe can be interacted with.
          <div style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: isStuck ? 'auto' : 'none', zIndex: 10000 }}>
            <div style={{ background: 'rgba(255,255,255,0.95)', padding: '8px 12px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', fontSize: 13, color: '#333', pointerEvents: 'auto' }}>
              {isStuck ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div>Preview failed to load.</div>
                  <button
                    onClick={(e) => {
                      try { e.stopPropagation(); } catch (err) {}
                      setIsStuck(false);
                      setPdfLoaded(false);
                      setLoadAttempts(0);
                      setIframeKey(`pdf-retry-manual-${Date.now()}`);
                    }}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1976d2', background: '#fff', color: '#1976d2', cursor: 'pointer' }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div>Loading preview…</div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Bottom controls removed - download/control moved to top-right overlay buttons */}
    </div>
  );
}

export default App;